import { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import type { ChangeEvent, DragEvent as ReactDragEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Autocomplete,
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Alert,
  Stepper,
  Step,
  StepLabel,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  Paper,
  LinearProgress,
  IconButton,
  Slider,
  Tooltip,
  FormControlLabel,
  Switch,
  alpha,
  useTheme,
} from '@mui/material';
import { lighten } from '@mui/material/styles';
import PsychologyIcon from '@mui/icons-material/Psychology';
import {
  ArrowBack,
  ArrowForward,
  CloudUpload,
  CheckCircle,
  Info,
  Lock,
  Phone as PhoneIcon,
  PhoneInTalk,
  PhoneIphone,
  Description,
  PhoneCallback,
  Sync,
  Build,
  Edit,
  Settings,
  Repeat,
  Hub,
  Code,
  Business,
  HelpOutline,
  Public,
  Schedule,
  AccessTime,
  Add,
  Delete,
  RestartAlt,
  Email,
  Campaign as CampaignIcon,
  CalendarMonth,
} from '@mui/icons-material';
import DataEditor, { GridCellKind } from '@glideapps/glide-data-grid';
import type { GridColumn, GridCell, Item } from '@glideapps/glide-data-grid';
import '@glideapps/glide-data-grid/dist/index.css';
import { CountrySelector, defaultCountries, parseCountry } from 'react-international-phone';
import 'react-international-phone/style.css';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import dayjs, { Dayjs } from 'dayjs';
import api from '../services/api';
import { uploadMediaFileDirect } from '../services/mediaUpload';
import { listAppointmentCalendars } from '../services/appointments';
import { useLanguage } from '../hooks/useLanguage';
import type { AIAgentProvider, Agent, AppointmentCalendar, DayOperatingHours, Script } from '../types';
import usePageTitle from '../hooks/usePageTitle';
import { useAuth } from '../contexts/AuthContext';
import ButtonTabs from '../components/ButtonTabs';
import CampaignConversionEmailControl from '../components/CampaignConversionEmailControl';
import DetailHeader from '../components/DetailHeader';
import ReverseIvrDtmfEditor from '../components/ReverseIvrDtmfEditor';
import WaveformPlayer from '../components/WaveformPlayer';
import WaveformPlayerField from '../components/WaveformPlayerField';
import { parseNotificationEmails } from '../utils/notificationEmails';
import { useNumberFormat } from '../hooks/useNumberFormat';
import { normalizeAgentInputDynamicVariables } from '../utils/agentDynamicVariables';
import {
  getReverseIvrAudioDisplayName,
  getReverseIvrAudioDownloadFilename,
  normalizeReverseIvrAudioOption,
} from '../utils/reverseIvrAudioOptions';
import {
  buildIvrDtmfMapPayload,
  createIvrRule,
  getIvrDtmfValidationErrorKey,
  type IvrDtmfRule,
} from '../utils/reverseIvrDtmf';
import {
  buildScheduleEntriesFromOperatingHours,
  DEFAULT_TENANT_OPERATING_HOURS,
  normalizeTenantOperatingHours,
} from '../utils/tenantOperatingHours';
import {
  ContactSourceSchemaPreviewError,
  formatSpreadsheetColumnIndexes,
  structureContactSourceRows,
} from '../utils/contactSourceColumns';
import type {
  ContactFilePreviewRequest,
  ContactFilePreviewResponse,
} from '../workers/contactFilePreview.types';

// ============================================================================
// Types
// ============================================================================

type CampaignCreationType = 'ai' | 'reverse_ivr';
type CampaignTransferTargetType = 'user' | 'group';

const IVR_ALLOWED_AUDIO_EXTENSIONS = ['aac', 'flac', 'm4a', 'mp3', 'ogg', 'wav'] as const;
const IVR_AUDIO_FILE_ACCEPT = IVR_ALLOWED_AUDIO_EXTENSIONS.map((extension) => `.${extension}`).join(',');

interface SipTrunkPoolOption {
  id: string;
  name: string;
  isActive?: boolean;
  maxConcurrentCalls?: number | null;
}

interface CampaignTransferUserOption {
  id: string;
  name: string;
  email: string;
  endpointType: 'internal_extension' | 'external_extension';
  extension: string | null;
}

interface CampaignTransferGroupOption {
  id: string;
  name: string;
  strategy: string;
  membersCount: number;
}

interface CampaignProviderResourcesOption {
  agents: Agent[];
}

interface ReverseIvrAudioOption {
  id: string;
  name: string;
  displayName?: string | null;
  originalFilename?: string | null;
  durationSeconds?: number | null;
  fileSize?: number;
}

interface CampaignBootstrapPayload {
  campaignTypes?: {
    available?: CampaignCreationType[];
    default?: CampaignCreationType | null;
  };
  limits?: {
    maxAllowedConcurrent?: Partial<Record<CampaignCreationType, number | null>>;
    ivr?: {
      ivrMaxAudioSeconds?: number;
      ivrMaxCallDurationSeconds?: number;
      ivrMaxPostAudioWaitSeconds?: number;
      ivrMaxAudioFileSizeBytes?: number;
      ivr_max_audio_seconds?: number;
      ivr_max_call_duration_seconds?: number;
      ivr_max_post_audio_wait_seconds?: number;
      ivr_max_audio_file_size_bytes?: number;
    };
  };
  defaults?: {
    operatingHours?: Record<string, { enabled: boolean; allDay?: boolean; all_day?: boolean; start: string | null; end: string | null }>;
    operating_hours?: Record<string, { enabled: boolean; allDay?: boolean; all_day?: boolean; start: string | null; end: string | null }>;
    scheduleEntries?: Array<{ days: string[]; allDay?: boolean; all_day?: boolean; start: string | null; end: string | null }>;
    schedule_entries?: Array<{ days: string[]; allDay?: boolean; all_day?: boolean; start: string | null; end: string | null }>;
  };
  resources?: {
    scripts?: Script[];
    sipTrunkPools?: SipTrunkPoolOption[];
    reverseIvrAudioOptions?: ReverseIvrAudioOption[];
    providers?: AIAgentProvider[];
    providerResources?: Record<string, CampaignProviderResourcesOption>;
  };
  transfer?: {
    telephonyPackEnabled?: boolean;
    telephony_pack_enabled?: boolean;
    users?: CampaignTransferUserOption[];
    groups?: CampaignTransferGroupOption[];
  };
}

// ============================================================================
// Constants
// ============================================================================

const PROVIDER_TYPE_LABELS: Record<string, string> = {
  cloud: 'Cloud',
  asterisk: 'Asterisk (SIP)',
  sip: 'External SIP',
  local: 'Local',
};

const DEFAULT_IVR_MAX_AUDIO_SECONDS = 27;
const CONTACT_PREVIEW_ROW_LIMIT = 200;
const STEP_BASIC_INFO = 0;
const STEP_AGENTS = 1;
const STEP_UPLOAD_CONTACTS = 2;
const STEP_CONFIGURE_COLUMNS = 3;
const STEP_AUTOMATION = 4;
const STEP_TRANSFERS = 5;
const STEP_CAMPAIGN_SETTINGS = 6;

function extractMediaPreviewUrl(payload: unknown): string | null {
  if (!payload || typeof payload !== 'object') {
    return null;
  }
  const data = payload as Record<string, unknown>;
  const candidate = data.downloadUrl ?? data.download_url ?? data.url;
  if (typeof candidate !== 'string') {
    return null;
  }
  const normalized = candidate.trim();
  return normalized.length > 0 ? normalized : null;
}

const getSteps = (t: (key: string) => string) => [
  { label: t('pages:campaigns.create.steps.basicInfo'), icon: <Info /> },
  { label: t('pages:campaigns.create.steps.agents'), icon: <PsychologyIcon /> },
  { label: t('pages:campaigns.create.steps.uploadContacts'), icon: <CloudUpload /> },
  { label: t('pages:campaigns.create.steps.configureColumns'), icon: <Description /> },
  { label: t('pages:campaigns.create.steps.automation'), icon: <Code /> },
  { label: t('pages:campaigns.create.steps.transfers'), icon: <PhoneInTalk /> },
  { label: t('pages:campaigns.create.steps.campaignSettings'), icon: <Settings /> },
];

// ============================================================================
// Component
// ============================================================================

export default function CampaignCreatePage() {
  const navigate = useNavigate();
  const { t } = useTranslation(['pages', 'common']);
  usePageTitle(t('pages:campaigns.title'));
  const { language } = useLanguage();
  const { user } = useAuth();
  const theme = useTheme();
  const { formatNumber, formatDecimal, formatPercent } = useNumberFormat();
  const scriptEventLabels = useMemo(
    () => (t('pages:scripts.eventTypes', { returnObjects: true }) || {}) as Record<string, string>,
    [t]
  );

  const dataGridTheme = useMemo(() => {
    const isLight = theme.palette.mode === 'light';
    const lightHeaderBase = theme.palette.primary.light;
    const lightHeader = lighten(lightHeaderBase, 0.6);
    const lightHeaderHover = lighten(lightHeaderBase, 0.45);
    const lightHeaderFocus = lighten(lightHeaderBase, 0.3);
    const headerBg = isLight ? lightHeader : theme.palette.action.selected;
    const headerHover = isLight ? lightHeaderHover : theme.palette.action.selected;
    const headerFocus = isLight ? lightHeaderFocus : theme.palette.action.selected;
    const headerText = theme.palette.text.primary;

    return {
      bgCell: theme.palette.background.paper,
      bgCellMedium: theme.palette.background.default,
      bgHeader: headerBg,
      bgHeaderHasFocus: headerFocus,
      bgHeaderHovered: headerHover,
      textDark: theme.palette.text.primary,
      textMedium: theme.palette.text.secondary,
      textLight: theme.palette.text.disabled,
      textHeader: headerText,
      textHeaderSelected: headerText,
      accentColor: theme.palette.primary.main,
      accentLight: alpha(theme.palette.primary.main, 0.15),
      borderColor: theme.palette.divider,
      horizontalBorderColor: theme.palette.divider,
      drilldownBorder: theme.palette.primary.main,
      bgSearchResult: alpha(theme.palette.warning.main, 0.25),
      textBubble: theme.palette.text.primary,
    };
  }, [theme]);
  
  // Helper to get localized country name using Intl.DisplayNames
  const getLocalizedCountryName = useCallback((iso2: string): string => {
    try {
      const displayNames = new Intl.DisplayNames([language], { type: 'region' });
      return displayNames.of(iso2.toUpperCase()) || iso2.toUpperCase();
    } catch {
      return iso2.toUpperCase();
    }
  }, [language]);
  
  // Helper to get country info for display
  const getCountryDisplayInfo = useCallback((iso2: string): { flag: string; dialCode: string; name: string } => {
    const country = defaultCountries.find(c => parseCountry(c).iso2 === iso2);
    if (country) {
      const parsed = parseCountry(country);
      return {
        flag: parsed.iso2,
        dialCode: parsed.dialCode,
        name: getLocalizedCountryName(parsed.iso2),
      };
    }
    return { flag: iso2, dialCode: '', name: getLocalizedCountryName(iso2) };
  }, [getLocalizedCountryName]);
  
  // Wizard state
  const [activeStep, setActiveStep] = useState(0);

  const [availableCampaignTypes, setAvailableCampaignTypes] = useState<CampaignCreationType[]>([]);
  const [selectedCampaignType, setSelectedCampaignType] = useState<CampaignCreationType>('ai');
  const [sipTrunkPools, setSipTrunkPools] = useState<SipTrunkPoolOption[]>([]);
  const [selectedSipTrunkPoolId, setSelectedSipTrunkPoolId] = useState<string>('');
  const [transferTelephonyPackEnabled, setTransferTelephonyPackEnabled] = useState(false);
  const [transferUsers, setTransferUsers] = useState<CampaignTransferUserOption[]>([]);
  const [transferGroups, setTransferGroups] = useState<CampaignTransferGroupOption[]>([]);
  const [transferTargetType, setTransferTargetType] = useState<CampaignTransferTargetType | ''>('');
  const [transferTargetId, setTransferTargetId] = useState<string>('');
  const [suspendWhenNoTransferOperatorAvailable, setSuspendWhenNoTransferOperatorAvailable] = useState(true);
  const [appointmentCalendars, setAppointmentCalendars] = useState<AppointmentCalendar[]>([]);
  const [appointmentCalendarsLoading, setAppointmentCalendarsLoading] = useState(false);
  const [appointmentCalendarsError, setAppointmentCalendarsError] = useState<string | null>(null);
  const [selectedAppointmentCalendarId, setSelectedAppointmentCalendarId] = useState<string>('');
  const [ivrAudioFile, setIvrAudioFile] = useState<File | null>(null);
  const [ivrAudioDisplayName, setIvrAudioDisplayName] = useState<string | null>(null);
  const [ivrAudioDurationSeconds, setIvrAudioDurationSeconds] = useState<number | null>(null);
  const [ivrAudioPreviewUrl, setIvrAudioPreviewUrl] = useState<string | null>(null);
  const [reverseIvrAudioOptions, setReverseIvrAudioOptions] = useState<ReverseIvrAudioOption[]>([]);
  const [selectedReverseIvrAudioId, setSelectedReverseIvrAudioId] = useState<string>('');
  const [selectedReverseIvrAudioPreviewUrl, setSelectedReverseIvrAudioPreviewUrl] = useState<string | null>(null);
  const [reverseIvrAudioPreviewLoading, setReverseIvrAudioPreviewLoading] = useState(false);
  const [reverseIvrUseNewAudio, setReverseIvrUseNewAudio] = useState(false);
  const [ivrAudioError, setIvrAudioError] = useState<string | null>(null);
  const [reverseIvrAudioFilenameSaving, setReverseIvrAudioFilenameSaving] = useState(false);
  const [ivrMaxAudioSeconds, setIvrMaxAudioSeconds] = useState<number>(DEFAULT_IVR_MAX_AUDIO_SECONDS);
  const [ivrMaxAudioFileSizeBytes, setIvrMaxAudioFileSizeBytes] = useState<number | null>(null);
  const [ivrDtmfRules, setIvrDtmfRules] = useState<IvrDtmfRule[]>([
    createIvrRule({ digit: '1', status: 'converted' }),
  ]);
  
  // Step 1: Basic Info
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [selectedProviderId, setSelectedProviderId] = useState<string | null>(null);
  
  // Step 2: Agent
  const [selectedAgentId, setSelectedAgentId] = useState<string>('');
  
  // Step 4 & 5: Contacts
  const [columns, setColumns] = useState<string[]>([]);
  const [phoneColumns, setPhoneColumns] = useState<string[]>([]);
  const [variableColumns, setVariableColumns] = useState<string[]>([]);
  const [displayNameColumn, setDisplayNameColumn] = useState<string | null>(null);
  const [fileName, setFileName] = useState('');
  const [fileData, setFileData] = useState<File | null>(null);
  const [contactsDropActive, setContactsDropActive] = useState(false);
  
  // Header configuration
  const [hasHeaderRow, setHasHeaderRow] = useState<boolean>(true);
  const [columnNames, setColumnNames] = useState<Record<number, string>>({});
  const [isEditingColumns, setIsEditingColumns] = useState<boolean>(false);
  
  // CSV configuration
  const [csvEncoding, setCsvEncoding] = useState<string>('UTF-8');
  const [csvDelimiter, setCsvDelimiter] = useState<string>(';');
  const [csvStringDelimiter, setCsvStringDelimiter] = useState<string>('"');
  const [isDetectingCsv, setIsDetectingCsv] = useState(false);
  
  // Contact validation options (fixed system rules)
  const [defaultCountryCode, setDefaultCountryCode] = useState<string>('br');
  const [phoneTypeFilter, setPhoneTypeFilter] = useState<string>('all');
  const [removePreviouslyConvertedContacts, setRemovePreviouslyConvertedContacts] = useState(false);
  const [removePreviouslyReachedContacts, setRemovePreviouslyReachedContacts] = useState(false);
  const [removePreviouslyOptoutContacts, setRemovePreviouslyOptoutContacts] = useState(false);
  
  // Preview state
  const [previewRawRows, setPreviewRawRows] = useState<string[][]>([]);
  const [previewTotalRows, setPreviewTotalRows] = useState(0);
  const [previewTruncated, setPreviewTruncated] = useState(false);
  const [previewHeaders, setPreviewHeaders] = useState<string[]>([]);
  const [previewRows, setPreviewRows] = useState<string[][]>([]);
  const [previewIgnoredColumnIndexes, setPreviewIgnoredColumnIndexes] = useState<number[]>([]);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewShowSearch, setPreviewShowSearch] = useState(false);
  const [previewSearchTerm, setPreviewSearchTerm] = useState('');
  const previewWorkerRef = useRef<Worker | null>(null);
  const previewRequestIdRef = useRef(0);

  // Step 7: Campaign Settings
  const [maxConcurrentCalls, setMaxConcurrentCalls] = useState(1);
  
  // Campaign Schedule
  interface ScheduleEntry {
    days: string[];
    allDay?: boolean;
    start: string | null;
    end: string | null;
  }
  interface ScheduleError {
    entryIndex: number;
    type: 'no_days' | 'time_invalid' | 'outside_limits' | 'overlap';
    message: string;
  }
  const [scheduleEntries, setScheduleEntries] = useState<ScheduleEntry[]>([]);
  const [tenantOperatingHours, setTenantOperatingHours] = useState<Record<string, DayOperatingHours>>(DEFAULT_TENANT_OPERATING_HOURS);
  const [scheduleErrors, setScheduleErrors] = useState<ScheduleError[]>([]);
  const [maxAllowedConcurrent, setMaxAllowedConcurrent] = useState<number | null>(null);
  const [maxAllowedConcurrentByType, setMaxAllowedConcurrentByType] = useState<Partial<Record<CampaignCreationType, number | null>>>({});
  const [maxRetries, setMaxRetries] = useState(3);
  const [retryDelayMinSeconds, setRetryDelayMinSeconds] = useState(0);
  const [retryDelayMaxSeconds, setRetryDelayMaxSeconds] = useState(0);
  const [selectedScripts, setSelectedScripts] = useState<string[]>([]);
  const [notificationEmailsInput, setNotificationEmailsInput] = useState('');
  const [notificationEmails, setNotificationEmails] = useState<string[]>([]);
  const [notificationEmailsError, setNotificationEmailsError] = useState<string | null>(null);
  const [automaticConversionEmailsEnabled, setAutomaticConversionEmailsEnabled] = useState(true);
  const notificationEmailsDefaultSet = useRef(false);
  
  // Data lists
  const [phoneProviders, setPhoneProviders] = useState<AIAgentProvider[]>([]);
  const [providerResourcesByProvider, setProviderResourcesByProvider] = useState<Record<string, CampaignProviderResourcesOption>>({});
  const [agents, setAgents] = useState<Agent[]>([]);
  const [scripts, setScripts] = useState<Script[]>([]);

  // UI state
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loadingData, setLoadingData] = useState(false);
  
  // Upload progress state
  const [uploadProgress, setUploadProgress] = useState<number | null>(null);
  const [submitPhase, setSubmitPhase] = useState<'uploading' | 'creating' | 'configuring' | null>(null);

  const normalizeScheduleEntriesForState = useCallback((entries: Array<Record<string, unknown>>): ScheduleEntry[] => (
    entries
      .filter((entry) => Array.isArray(entry.days))
      .map((entry) => {
        const allDay = Boolean(entry.allDay ?? entry.all_day);
        return {
          days: (entry.days as unknown[]).map(String),
          allDay,
          start: allDay ? null : String(entry.start || ''),
          end: allDay ? null : String(entry.end || ''),
        };
      })
  ), []);

  // ============================================================================
  // Data Loading
  // ============================================================================

  useEffect(() => {
    const loadData = async () => {
      setLoadingData(true);
      try {
        const response = await api.get('/api/campaigns/bootstrap/');
        const payload: CampaignBootstrapPayload = response.data || {};

        const availableTypesRaw = payload.campaignTypes?.available ?? [];
        const availableTypes: CampaignCreationType[] = Array.isArray(availableTypesRaw)
          ? availableTypesRaw.filter(
            (entry): entry is CampaignCreationType => entry === 'ai' || entry === 'reverse_ivr',
          )
          : [];
        const defaultType = payload.campaignTypes?.default;

        setAvailableCampaignTypes(availableTypes);
        if (availableTypes.length > 0) {
          setError(null);
          setSelectedCampaignType((current) => {
            if (availableTypes.includes(current)) {
              return current;
            }
            if (defaultType && availableTypes.includes(defaultType)) {
              return defaultType;
            }
            return availableTypes[0];
          });
        } else {
          setSelectedCampaignType('ai');
          setError(t('pages:campaigns.create.basicInfo.noCampaignTypeAvailable'));
        }

        const providersRaw = payload.resources?.providers ?? [];
        const providers = Array.isArray(providersRaw) ? providersRaw : [];
        const activeProviders = providers.filter((provider: AIAgentProvider & { is_active?: boolean }) => (
          provider.isActive === true || provider.is_active === true
        ));
        setPhoneProviders(activeProviders);

        const providerResourcesRaw = payload.resources?.providerResources ?? {};
        setProviderResourcesByProvider(providerResourcesRaw);

        setSelectedProviderId((current) => {
          if (activeProviders.length === 0) {
            return null;
          }
          if (current && activeProviders.some((provider) => provider.id === current)) {
            return current;
          }
          return activeProviders[0].id;
        });

        const scriptsDataRaw = payload.resources?.scripts ?? [];
        const scriptsData = Array.isArray(scriptsDataRaw) ? scriptsDataRaw : [];
        setScripts(scriptsData.filter((script: Script & { is_active?: boolean }) => (
          script.isActive === true || script.is_active === true
        )));

        const sipTrunkPoolsRaw = payload.resources?.sipTrunkPools ?? [];
        const allSipTrunkPools = Array.isArray(sipTrunkPoolsRaw) ? sipTrunkPoolsRaw : [];
        const activeSipTrunkPools = allSipTrunkPools.filter((pool: SipTrunkPoolOption & { is_active?: boolean }) => (
          pool.isActive ?? pool.is_active ?? true
        ));
        setSipTrunkPools(activeSipTrunkPools);
        if (activeSipTrunkPools.length === 1) {
          setSelectedSipTrunkPoolId((current) => current || activeSipTrunkPools[0].id);
        }

        const reverseIvrAudioOptionsRaw = (
          payload.resources?.reverseIvrAudioOptions
          ?? (payload.resources as { reverse_ivr_audio_options?: ReverseIvrAudioOption[] } | undefined)?.reverse_ivr_audio_options
          ?? []
        );
        const reverseIvrAudioOptionsData = Array.isArray(reverseIvrAudioOptionsRaw)
          ? reverseIvrAudioOptionsRaw.map((option) => normalizeReverseIvrAudioOption(option))
          : [];
        setReverseIvrAudioOptions(reverseIvrAudioOptionsData);
        setSelectedReverseIvrAudioId((current) => {
          if (current && reverseIvrAudioOptionsData.some((option) => option.id === current)) {
            return current;
          }
          if (reverseIvrAudioOptionsData.length === 1) {
            return reverseIvrAudioOptionsData[0].id;
          }
          return '';
        });

        const maxAllowedByType = (
          payload.limits?.maxAllowedConcurrent
          ?? (payload.limits as { max_allowed_concurrent?: Record<string, unknown> } | undefined)?.max_allowed_concurrent
          ?? {}
        ) as Record<string, unknown>;
        const reverseIvrMaxAllowed = maxAllowedByType.reverseIvr ?? maxAllowedByType.reverse_ivr;
        const normalizedMaxAllowed: Partial<Record<CampaignCreationType, number | null>> = {
          ai: Number.isFinite(Number(maxAllowedByType.ai)) ? Number(maxAllowedByType.ai) : null,
          reverse_ivr: Number.isFinite(Number(reverseIvrMaxAllowed))
            ? Number(reverseIvrMaxAllowed)
            : null,
        };
        setMaxAllowedConcurrentByType(normalizedMaxAllowed);

        const normalizedHours = normalizeTenantOperatingHours(
          payload.defaults?.operatingHours ?? payload.defaults?.operating_hours
        );
        setTenantOperatingHours(normalizedHours);
        const scheduleEntriesRaw = payload.defaults?.scheduleEntries ?? payload.defaults?.schedule_entries;
        if (Array.isArray(scheduleEntriesRaw) && scheduleEntriesRaw.length > 0) {
          setScheduleEntries(normalizeScheduleEntriesForState(scheduleEntriesRaw as Array<Record<string, unknown>>));
        } else {
          setScheduleEntries(buildScheduleEntriesFromOperatingHours(normalizedHours));
        }

        const rawIvrMaxAudioSeconds = Number(
          payload.limits?.ivr?.ivrMaxAudioSeconds
            ?? payload.limits?.ivr?.ivr_max_audio_seconds
        );
        const rawIvrMaxAudioFileSizeBytes = Number(
          payload.limits?.ivr?.ivrMaxAudioFileSizeBytes
            ?? payload.limits?.ivr?.ivr_max_audio_file_size_bytes
        );
        setIvrMaxAudioSeconds(
          Number.isFinite(rawIvrMaxAudioSeconds) && rawIvrMaxAudioSeconds > 0
            ? Math.floor(rawIvrMaxAudioSeconds)
            : DEFAULT_IVR_MAX_AUDIO_SECONDS
        );
        setIvrMaxAudioFileSizeBytes(
          Number.isFinite(rawIvrMaxAudioFileSizeBytes) && rawIvrMaxAudioFileSizeBytes > 0
            ? Math.floor(rawIvrMaxAudioFileSizeBytes)
            : null
        );

        const transferPayload = payload.transfer ?? {};
        const usersData = Array.isArray(transferPayload.users) ? transferPayload.users : [];
        const groupsData = Array.isArray(transferPayload.groups) ? transferPayload.groups : [];
        setTransferTelephonyPackEnabled(Boolean(
          transferPayload.telephonyPackEnabled ?? transferPayload.telephony_pack_enabled,
        ));
        setTransferUsers(usersData.map((entry: CampaignTransferUserOption & { endpoint_type?: string }) => ({
          id: entry.id,
          name: entry.name || entry.email || entry.id,
          email: entry.email || '',
          endpointType: (entry.endpointType ?? entry.endpoint_type ?? 'internal_extension') as 'internal_extension' | 'external_extension',
          extension: entry.extension ?? null,
        })));
        setTransferGroups(groupsData.map((entry: CampaignTransferGroupOption & { members_count?: number }) => ({
          id: entry.id,
          name: entry.name || entry.id,
          strategy: entry.strategy || '',
          membersCount: Number(entry.membersCount ?? entry.members_count ?? 0),
        })));
      } catch (err) {
        console.error('Error loading data:', err);
        setReverseIvrAudioOptions([]);
        setSelectedReverseIvrAudioId('');
        setIvrMaxAudioFileSizeBytes(null);
      } finally {
        setLoadingData(false);
      }
    };
    loadData();
  }, [normalizeScheduleEntriesForState, t]);

  const loadAppointmentCalendars = useCallback(async () => {
    setAppointmentCalendarsLoading(true);
    setAppointmentCalendarsError(null);
    try {
      const payload = await listAppointmentCalendars({
        page: 1,
        pageSize: 200,
        isActive: true,
        ordering: 'name',
      });
      setAppointmentCalendars(Array.isArray(payload.results) ? payload.results : []);
    } catch (requestError: unknown) {
      console.error('Error loading appointment calendars:', requestError);
      setAppointmentCalendars([]);
      setAppointmentCalendarsError(t('pages:campaigns.create.appointments.loadError'));
    } finally {
      setAppointmentCalendarsLoading(false);
    }
  }, [t]);

  useEffect(() => {
    loadAppointmentCalendars();
  }, [loadAppointmentCalendars]);

  useEffect(() => {
    const nextMaxAllowed = maxAllowedConcurrentByType[selectedCampaignType] ?? null;
    setMaxAllowedConcurrent(nextMaxAllowed);
    setMaxConcurrentCalls((prev) => {
      const base = Math.max(1, prev || 1);
      if (!nextMaxAllowed) {
        return base;
      }
      return Math.min(base, nextMaxAllowed);
    });
  }, [maxAllowedConcurrentByType, selectedCampaignType]);

  useEffect(() => {
    if (selectedCampaignType !== 'ai' || !selectedProviderId) {
      setAgents([]);
      return;
    }

    const resources = providerResourcesByProvider[selectedProviderId];
    setAgents(resources?.agents ?? []);
  }, [providerResourcesByProvider, selectedCampaignType, selectedProviderId]);

  useEffect(() => {
    if (selectedCampaignType !== 'ai') {
      setTransferTargetType('');
      setTransferTargetId('');
      setSuspendWhenNoTransferOperatorAvailable(true);
    }
  }, [selectedCampaignType]);

  useEffect(() => {
    if (selectedCampaignType === 'reverse_ivr') {
      return;
    }
    setReverseIvrUseNewAudio(false);
    setSelectedReverseIvrAudioId('');
    setSelectedReverseIvrAudioPreviewUrl(null);
    setIvrAudioFile(null);
    setIvrAudioDurationSeconds(null);
    setIvrAudioError(null);
    if (ivrAudioPreviewUrl) {
      URL.revokeObjectURL(ivrAudioPreviewUrl);
      setIvrAudioPreviewUrl(null);
    }
  }, [ivrAudioPreviewUrl, selectedCampaignType]);

  useEffect(() => {
    if (reverseIvrUseNewAudio) {
      setSelectedReverseIvrAudioId('');
      setSelectedReverseIvrAudioPreviewUrl(null);
      return;
    }
    setIvrAudioFile(null);
    setIvrAudioDurationSeconds(null);
    setIvrAudioError(null);
    if (ivrAudioPreviewUrl) {
      URL.revokeObjectURL(ivrAudioPreviewUrl);
      setIvrAudioPreviewUrl(null);
    }
  }, [ivrAudioPreviewUrl, reverseIvrUseNewAudio]);

  useEffect(() => {
    if (reverseIvrUseNewAudio || !selectedReverseIvrAudioId) {
      setSelectedReverseIvrAudioPreviewUrl(null);
      setReverseIvrAudioPreviewLoading(false);
      return;
    }

    let isMounted = true;
    setReverseIvrAudioPreviewLoading(true);
    api.get(`/api/media/${selectedReverseIvrAudioId}/`)
      .then((response) => {
        if (!isMounted) {
          return;
        }
        setSelectedReverseIvrAudioPreviewUrl(extractMediaPreviewUrl(response?.data));
      })
      .catch((previewError) => {
        if (!isMounted) {
          return;
        }
        console.error('Error loading reverse IVR media preview:', previewError);
        setSelectedReverseIvrAudioPreviewUrl(null);
      })
      .finally(() => {
        if (!isMounted) {
          return;
        }
        setReverseIvrAudioPreviewLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, [reverseIvrUseNewAudio, selectedReverseIvrAudioId]);

  useEffect(() => {
    setTransferTargetId('');
  }, [transferTargetType]);

  useEffect(() => {
    if (!notificationEmailsDefaultSet.current && user?.email) {
      const normalized = user.email.trim().toLowerCase();
      if (normalized) {
        setNotificationEmailsInput(normalized);
        setNotificationEmails([normalized]);
        notificationEmailsDefaultSet.current = true;
      }
    }
  }, [user]);

  const validateNotificationEmails = (value: string) => {
    const emails = parseNotificationEmails(value);
    if (emails.length === 0) {
      return { emails: [], error: null };
    }
    const invalidEmails = emails.filter((email) => !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email));
    if (invalidEmails.length > 0) {
      return {
        emails,
        error: t('pages:campaigns.create.settings.notificationEmailsInvalid', { count: invalidEmails.length }),
      };
    }
    return { emails, error: null };
  };

  const handleNotificationEmailsChange = (value: string) => {
    setNotificationEmailsInput(value);
    const { emails, error: validationError } = validateNotificationEmails(value);
    setNotificationEmailsError(validationError);
    setNotificationEmails(emails);
  };

  const clampMaxConcurrent = useCallback((value: number) => {
    const normalized = Math.max(1, value || 1);
    if (maxAllowedConcurrent === null || maxAllowedConcurrent === 0) {
      return 1;
    }
    return Math.min(normalized, maxAllowedConcurrent);
  }, [maxAllowedConcurrent]);

  useEffect(() => {
    if (maxAllowedConcurrent) {
      setMaxConcurrentCalls((current) => clampMaxConcurrent(current));
    }
  }, [maxAllowedConcurrent, clampMaxConcurrent]);

  useEffect(() => {
    return () => {
      if (ivrAudioPreviewUrl) {
        URL.revokeObjectURL(ivrAudioPreviewUrl);
      }
    };
  }, [ivrAudioPreviewUrl]);

  const hasAvailableCampaignTypes = availableCampaignTypes.length > 0;
  const isAiCampaign = selectedCampaignType === 'ai';
  const isReverseIvrCampaign = selectedCampaignType === 'reverse_ivr';
  const hasSelectedAgentRequiringPreCall = useMemo(() => {
    if (!isAiCampaign || !selectedAgentId) {
      return false;
    }
    return agents.some(
      (agent) => String(agent.id) === String(selectedAgentId)
        && Boolean(agent.requiresPreCallScript),
    );
  }, [agents, isAiCampaign, selectedAgentId]);
  const hasAutomationStep = scripts.length > 0 || hasSelectedAgentRequiringPreCall;
  const hasSelectedAgentRequiringTransfer = useMemo(() => {
    if (!isAiCampaign || !selectedAgentId) {
      return false;
    }
    return agents.some(
      (agent) => (
        String(agent.id) === String(selectedAgentId)
        && agent.isActive
        && agent.aiAgentProviderId === selectedProviderId
        && Boolean(agent.transferTargetRequired)
      ),
    );
  }, [agents, isAiCampaign, selectedAgentId, selectedProviderId]);
  const hasSelectedAgentRequiringAppointmentCalendar = useMemo(() => {
    if (!isAiCampaign || !selectedAgentId) {
      return false;
    }
    return agents.some(
      (agent) => (
        String(agent.id) === String(selectedAgentId)
        && agent.isActive
        && agent.aiAgentProviderId === selectedProviderId
        && Boolean(agent.appointmentCalendarRequired)
      ),
    );
  }, [agents, isAiCampaign, selectedAgentId, selectedProviderId]);
  const hasTransferStep = (
    isAiCampaign
    && transferTelephonyPackEnabled
    && transferUsers.length > 0
    && hasSelectedAgentRequiringTransfer
  );
  const isCampaignTypeSelectable = availableCampaignTypes.length > 1;
  const hasFiniteMaxConcurrent = maxAllowedConcurrent !== null && maxAllowedConcurrent > 0;
  const allSteps = useMemo(() => getSteps(t), [t]);
  const visibleStepIndexes = useMemo(
    () => {
      if (isAiCampaign) {
        return [
          STEP_BASIC_INFO,
          STEP_AGENTS,
          STEP_UPLOAD_CONTACTS,
          STEP_CONFIGURE_COLUMNS,
          ...(hasAutomationStep ? [STEP_AUTOMATION] : []),
          ...(hasTransferStep ? [STEP_TRANSFERS] : []),
          STEP_CAMPAIGN_SETTINGS,
        ];
      }
      return [
        STEP_BASIC_INFO,
        STEP_UPLOAD_CONTACTS,
        STEP_CONFIGURE_COLUMNS,
        ...(hasAutomationStep ? [STEP_AUTOMATION] : []),
        STEP_CAMPAIGN_SETTINGS,
      ];
    },
    [hasAutomationStep, hasTransferStep, isAiCampaign],
  );
  const visibleSteps = useMemo(
    () => visibleStepIndexes.map((stepIndex) => allSteps[stepIndex]),
    [allSteps, visibleStepIndexes],
  );
  const activeStepperIndex = useMemo(() => {
    const visibleIndex = visibleStepIndexes.indexOf(activeStep);
    return visibleIndex >= 0 ? visibleIndex : 0;
  }, [activeStep, visibleStepIndexes]);

  useEffect(() => {
    if (!hasTransferStep) {
      setTransferTargetType('');
      setTransferTargetId('');
      setSuspendWhenNoTransferOperatorAvailable(true);
    }
  }, [hasTransferStep]);

  useEffect(() => {
    if (!hasSelectedAgentRequiringAppointmentCalendar) {
      setSelectedAppointmentCalendarId('');
      return;
    }
    if (appointmentCalendars.length === 1) {
      setSelectedAppointmentCalendarId((current) => current || appointmentCalendars[0].id);
      return;
    }
    if (
      selectedAppointmentCalendarId
      && !appointmentCalendars.some((calendar) => calendar.id === selectedAppointmentCalendarId)
    ) {
      setSelectedAppointmentCalendarId('');
    }
  }, [
    appointmentCalendars,
    hasSelectedAgentRequiringAppointmentCalendar,
    selectedAppointmentCalendarId,
  ]);

  useEffect(() => {
    if (isReverseIvrCampaign && activeStep === STEP_AGENTS) {
      setActiveStep(STEP_UPLOAD_CONTACTS);
      return;
    }
    if (isReverseIvrCampaign && activeStep === STEP_TRANSFERS) {
      setActiveStep(hasAutomationStep ? STEP_AUTOMATION : STEP_CAMPAIGN_SETTINGS);
      return;
    }
    if (!hasAutomationStep && activeStep === STEP_AUTOMATION) {
      setActiveStep(hasTransferStep ? STEP_TRANSFERS : STEP_CAMPAIGN_SETTINGS);
      return;
    }
    if (!hasTransferStep && activeStep === STEP_TRANSFERS) {
      setActiveStep(STEP_CAMPAIGN_SETTINGS);
    }
  }, [activeStep, hasAutomationStep, hasTransferStep, isReverseIvrCampaign]);

  const ivrDtmfValidationErrorKey = useMemo(
    () => getIvrDtmfValidationErrorKey(ivrDtmfRules),
    [ivrDtmfRules],
  );

  const resolveAudioDurationSeconds = useCallback((file: File): Promise<number | null> => (
    new Promise((resolve) => {
      const tempUrl = URL.createObjectURL(file);
      const audio = new Audio(tempUrl);
      audio.onloadedmetadata = () => {
        const duration = Number.isFinite(audio.duration) ? Number(audio.duration) : null;
        URL.revokeObjectURL(tempUrl);
        resolve(duration);
      };
      audio.onerror = () => {
        URL.revokeObjectURL(tempUrl);
        resolve(null);
      };
    })
  ), []);

  const handleIvrAudioSelect = useCallback(async (file: File) => {
    setError(null);
    setIvrAudioError(null);

    const extension = file.name.split('.').pop()?.trim().toLowerCase() || '';
    if (!IVR_ALLOWED_AUDIO_EXTENSIONS.includes(extension as typeof IVR_ALLOWED_AUDIO_EXTENSIONS[number])) {
      setIvrAudioError(
        t('pages:campaigns.create.errors.ivrAudioUnsupportedFormat', {
          formats: IVR_ALLOWED_AUDIO_EXTENSIONS.join(', '),
        }),
      );
      return;
    }

    const durationSeconds = await resolveAudioDurationSeconds(file);
    if (durationSeconds !== null && durationSeconds > ivrMaxAudioSeconds) {
      setIvrAudioError(
        t('pages:campaigns.create.errors.ivrAudioTooLong', {
          maxSeconds: ivrMaxAudioSeconds,
        }),
      );
      return;
    }

    if (ivrAudioPreviewUrl) {
      URL.revokeObjectURL(ivrAudioPreviewUrl);
    }

    setIvrAudioError(null);
    setIvrAudioFile(file);
    setIvrAudioDisplayName(file.name);
    setIvrAudioDurationSeconds(durationSeconds);
    setIvrAudioPreviewUrl(URL.createObjectURL(file));
  }, [ivrAudioPreviewUrl, ivrMaxAudioSeconds, resolveAudioDurationSeconds, t]);

  const handleIvrAudioDisplayNameCommit = useCallback(async (nextDisplayName: string) => {
    if (reverseIvrUseNewAudio && ivrAudioFile) {
      setIvrAudioDisplayName(nextDisplayName);
      return;
    }

    if (!selectedReverseIvrAudioId) {
      return;
    }

    setReverseIvrAudioFilenameSaving(true);
    setIvrAudioError(null);
    try {
      const response = await api.post(`/api/media/${selectedReverseIvrAudioId}/rename/`, {
        displayName: nextDisplayName,
      });
      const updatedDisplayName = String(
        response.data?.displayName
        ?? response.data?.display_name
        ?? nextDisplayName,
      ).trim();
      const updatedOriginalFilename = response.data?.originalFilename
        ?? response.data?.original_filename
        ?? null;
      setReverseIvrAudioOptions((prev) => prev.map((option) => (
        option.id === selectedReverseIvrAudioId
          ? normalizeReverseIvrAudioOption({
            ...option,
            displayName: updatedDisplayName,
            name: updatedDisplayName,
            originalFilename: updatedOriginalFilename,
          })
          : option
      )));
    } catch (renameError) {
      console.error('Failed to rename reverse IVR audio:', renameError);
      setIvrAudioError(t('pages:campaigns.create.errors.ivrAudioRenameFailed'));
    } finally {
      setReverseIvrAudioFilenameSaving(false);
    }
  }, [
    ivrAudioFile,
    reverseIvrUseNewAudio,
    selectedReverseIvrAudioId,
    t,
  ]);

  // ============================================================================
  // Derived Data
  // ============================================================================

  const selectedProvider = phoneProviders.find(p => p.id === selectedProviderId);
  const selectedSipTrunkPool = sipTrunkPools.find((pool) => pool.id === selectedSipTrunkPoolId);
  const selectedReverseIvrAudioOption = reverseIvrAudioOptions.find(
    (audioOption) => audioOption.id === selectedReverseIvrAudioId,
  ) || null;
  const hasSelectedReverseIvrAudio = reverseIvrUseNewAudio
    ? Boolean(ivrAudioFile)
    : Boolean(selectedReverseIvrAudioId);
  const reverseIvrAudioSummaryLabel = reverseIvrUseNewAudio
    ? (ivrAudioDisplayName || ivrAudioFile?.name || '—')
    : getReverseIvrAudioDisplayName(selectedReverseIvrAudioOption);
  
  const availableAgents = agents.filter(agent =>
    isAiCampaign && agent.isActive && agent.aiAgentProviderId === selectedProviderId
  );
  const selectedAgent = availableAgents.find((agent) => String(agent.id) === String(selectedAgentId)) || null;
  const selectedAppointmentCalendar = appointmentCalendars.find(
    (calendar) => calendar.id === selectedAppointmentCalendarId,
  ) || null;
  const selectedAgentDynamicVariables = useMemo(() => {
    if (!selectedAgent?.config || typeof selectedAgent.config !== 'object') {
      return {};
    }
    const config = selectedAgent.config as Record<string, unknown>;
    return normalizeAgentInputDynamicVariables(config.dynamicVariables);
  }, [selectedAgent]);
  const selectedAgentRequiredVariables = useMemo(
    () => Object.keys(selectedAgentDynamicVariables),
    [selectedAgentDynamicVariables],
  );
  const selectedAgentOptionalVariables: string[] = [];
  const availableDynamicVariableHeaders = useMemo(
    () => columns.filter((column) => !phoneColumns.includes(column)),
    [columns, phoneColumns],
  );
  const missingRequiredDynamicVariables = useMemo(() => {
    if (!isAiCampaign || selectedAgentRequiredVariables.length === 0) {
      return [];
    }
    const availableHeaders = new Set(
      availableDynamicVariableHeaders
        .map((header) => header.trim())
        .filter(Boolean),
    );
    return selectedAgentRequiredVariables.filter((variable) => !availableHeaders.has(String(variable || '').trim()));
  }, [availableDynamicVariableHeaders, isAiCampaign, selectedAgentRequiredVariables]);
  const sourceColumnAliases = useMemo<Record<string, string>>(
    () => previewHeaders.reduce<Record<string, string>>((accumulator, originalHeader, index) => {
      const canonicalHeader = columns[index];
      if (canonicalHeader && canonicalHeader !== originalHeader) {
        accumulator[originalHeader] = canonicalHeader;
      }
      return accumulator;
    }, {}),
    [columns, previewHeaders],
  );

  // Auto-select agent if only one available
  useEffect(() => {
    if (!isAiCampaign) {
      return;
    }
    if (availableAgents.length === 1 && !selectedAgentId) {
      setSelectedAgentId(availableAgents[0].id);
      return;
    }
    if (
      selectedAgentId
      && !availableAgents.some((agent) => String(agent.id) === String(selectedAgentId))
    ) {
      setSelectedAgentId('');
    }
  }, [availableAgents, isAiCampaign, selectedAgentId]);

  // ============================================================================
  // File Processing
  // ============================================================================

  const structureData = (
    rawRows: string[][],
    useFirstRowAsHeader: boolean,
    customNames?: Record<number, string>
  ): { headers: string[]; rows: Record<string, string>[]; ignoredColumnIndexes: number[] } => {
    return structureContactSourceRows(rawRows, useFirstRowAsHeader, customNames);
  };

  const formatPreviewError = useCallback((err: unknown): string => {
    if (err instanceof ContactSourceSchemaPreviewError) {
      const columns = err.details.join(', ');
      if (err.code === 'unnamed_columns_with_data') {
        return t('pages:campaigns.create.uploadContacts.unnamedColumnsWithData', { columns });
      }
      if (err.code === 'duplicate_headers') {
        return t('pages:campaigns.create.uploadContacts.duplicateHeaders', { columns });
      }
      return t('pages:campaigns.create.uploadContacts.noNamedColumns');
    }
    if (
      err instanceof Error
      && (err.message === 'No data found in file' || err.message === 'No data rows found in file')
    ) {
      return t('pages:campaigns.create.errors.noData');
    }
    return err instanceof Error ? err.message : t('pages:campaigns.create.errors.previewFailed');
  }, [t]);

  const loadPreviewData = useCallback(async (
    file: File,
    encoding: string,
    delimiter: string,
    stringDelim: string,
  ) => {
    previewWorkerRef.current?.terminate();

    const requestId = previewRequestIdRef.current + 1;
    previewRequestIdRef.current = requestId;

    const worker = new Worker(
      new URL('../workers/contactFilePreview.worker.ts', import.meta.url),
      { type: 'module' },
    );
    previewWorkerRef.current = worker;

    setPreviewLoading(true);
    setPreviewError(null);

    try {
      const response = await new Promise<ContactFilePreviewResponse>((resolve, reject) => {
        worker.onmessage = (event: MessageEvent<ContactFilePreviewResponse>) => {
          resolve(event.data);
        };
        worker.onerror = () => {
          reject(new Error('Error generating file preview'));
        };

        const request: ContactFilePreviewRequest = {
          requestId,
          file,
          kind: file.name.endsWith('.xlsx') || file.name.endsWith('.xls') ? 'excel' : 'csv',
          encoding,
          delimiter,
          quoteChar: stringDelim,
          previewLimit: CONTACT_PREVIEW_ROW_LIMIT,
        };

        worker.postMessage(request);
      });

      if (response.requestId !== previewRequestIdRef.current) {
        return;
      }

      if (!response.success) {
        throw new Error(response.error);
      }

      setPreviewRawRows(response.rawRows);
      setPreviewTotalRows(response.totalRows);
      setPreviewTruncated(response.truncated);
    } catch (err) {
      if (requestId !== previewRequestIdRef.current) {
        return;
      }
      setPreviewRawRows([]);
      setPreviewTotalRows(0);
      setPreviewTruncated(false);
      setPreviewHeaders([]);
      setPreviewRows([]);
      setPreviewIgnoredColumnIndexes([]);
      setPreviewError(formatPreviewError(err));
    } finally {
      if (requestId === previewRequestIdRef.current) {
        setPreviewLoading(false);
      }
      worker.terminate();
      if (previewWorkerRef.current === worker) {
        previewWorkerRef.current = null;
      }
    }
  }, [formatPreviewError]);

  useEffect(() => {
    return () => {
      previewWorkerRef.current?.terminate();
      previewWorkerRef.current = null;
    };
  }, []);

  useEffect(() => {
    if (!fileData) {
      setPreviewRawRows([]);
      setPreviewTotalRows(0);
      setPreviewTruncated(false);
      setPreviewHeaders([]);
      setPreviewRows([]);
      setPreviewIgnoredColumnIndexes([]);
      setPreviewError(null);
      setPreviewLoading(false);
      return;
    }

    setPreviewShowSearch(false);
    setPreviewSearchTerm('');
    loadPreviewData(fileData, csvEncoding, csvDelimiter, csvStringDelimiter);
  }, [fileData, csvEncoding, csvDelimiter, csvStringDelimiter, loadPreviewData]);

  useEffect(() => {
    if (previewRawRows.length === 0) {
      setPreviewHeaders([]);
      setPreviewRows([]);
      setPreviewIgnoredColumnIndexes([]);
      return;
    }

    try {
      const { headers, rows, ignoredColumnIndexes } = structureData(previewRawRows, hasHeaderRow);
      setPreviewError(null);
      setPreviewHeaders(headers);
      setPreviewRows(rows.map((row) => headers.map((header) => row[header] || '')));
      setPreviewIgnoredColumnIndexes(ignoredColumnIndexes);
    } catch (err) {
      setPreviewHeaders([]);
      setPreviewRows([]);
      setPreviewIgnoredColumnIndexes([]);
      setPreviewError(formatPreviewError(err));
    }
  }, [formatPreviewError, previewRawRows, hasHeaderRow]);

  const filteredPreviewRows = useMemo(() => {
    const normalizedSearch = previewSearchTerm.trim().toLowerCase();
    if (!normalizedSearch) {
      return previewRows;
    }
    return previewRows.filter((row) =>
      row.some((cell) => (cell || '').toLowerCase().includes(normalizedSearch))
    );
  }, [previewRows, previewSearchTerm]);

  const importedRowsCount = useMemo(() => {
    const headerOffset = hasHeaderRow ? 1 : 0;
    const effectiveRawRows = previewTruncated ? previewTotalRows : previewRawRows.length;
    return Math.max(0, effectiveRawRows - headerOffset);
  }, [hasHeaderRow, previewRawRows.length, previewTotalRows, previewTruncated]);
  const previewIgnoredColumnsText = useMemo(
    () => formatSpreadsheetColumnIndexes(previewIgnoredColumnIndexes),
    [previewIgnoredColumnIndexes],
  );

  const handleTogglePreviewSearch = useCallback(() => {
    setPreviewShowSearch((previous) => {
      const nextState = !previous;
      if (!nextState) {
        setPreviewSearchTerm('');
      }
      return nextState;
    });
  }, []);

  useEffect(() => {
    if (activeStep === STEP_CONFIGURE_COLUMNS && previewHeaders.length > 0) {
      setColumns(previewHeaders);
      setColumnNames({});
      
      const phoneKeywords = ['phone', 'telefone', 'numero', 'número', 'celular', 'mobile', 'fone', 'tel', 'whatsapp'];
      const detectedPhoneCols = previewHeaders.filter(h => 
        phoneKeywords.some(kw => h.toLowerCase().includes(kw))
      );
      setPhoneColumns(detectedPhoneCols);
      
      const otherColumns = previewHeaders.filter(h => !detectedPhoneCols.includes(h));
      setVariableColumns(otherColumns);
      
      const namePattern = /name|nome|nm/i;
      const detectedNameCol = previewHeaders.find(h => namePattern.test(h));
      setDisplayNameColumn(detectedNameCol || previewHeaders[0] || null);
    }
  }, [previewHeaders, activeStep]);

  const detectCsvSettings = async (file: File): Promise<{
    encoding: string;
    delimiter: string;
    quoteChar: string;
  }> => {
    return new Promise((resolve) => {
      const reader = new FileReader();
      
      reader.onload = (e) => {
        const buffer = e.target?.result as ArrayBuffer;
        const bytes = new Uint8Array(buffer);
        
        let encoding = 'UTF-8';
        
        if (bytes[0] === 0xEF && bytes[1] === 0xBB && bytes[2] === 0xBF) {
          encoding = 'UTF-8';
        } else if (bytes[0] === 0xFF && bytes[1] === 0xFE) {
          encoding = 'UTF-16';
        } else if (bytes[0] === 0xFE && bytes[1] === 0xFF) {
          encoding = 'UTF-16';
        } else {
          let hasHighBytes = false;
          let hasInvalidUtf8 = false;
          
          for (let i = 0; i < Math.min(bytes.length, 10000); i++) {
            if (bytes[i] > 127) {
              hasHighBytes = true;
              if (bytes[i] >= 0xC0 && bytes[i] <= 0xDF) {
                if (i + 1 >= bytes.length || bytes[i + 1] < 0x80 || bytes[i + 1] > 0xBF) {
                  hasInvalidUtf8 = true;
                  break;
                }
              } else if (bytes[i] >= 0xE0 && bytes[i] <= 0xEF) {
                if (i + 2 >= bytes.length) {
                  hasInvalidUtf8 = true;
                  break;
                }
              } else if (bytes[i] >= 0x80 && bytes[i] <= 0xBF) {
                hasInvalidUtf8 = true;
                break;
              }
            }
          }
          
          if (hasHighBytes && hasInvalidUtf8) {
            encoding = 'ISO-8859-1';
          }
        }
        
        const decoder = new TextDecoder(encoding);
        const text = decoder.decode(buffer);
        const firstLines = text.split('\n').slice(0, 5).join('\n');
        
        const firstLine = text.split('\n')[0] || '';
        const delimiters = [
          { char: ';', count: (firstLine.match(/;/g) || []).length },
          { char: ',', count: (firstLine.match(/,/g) || []).length },
          { char: '\t', count: (firstLine.match(/\t/g) || []).length },
          { char: '|', count: (firstLine.match(/\|/g) || []).length },
        ];
        
        delimiters.sort((a, b) => b.count - a.count);
        const delimiter = delimiters[0].count > 0 ? delimiters[0].char : ';';
        
        let quoteChar = '"';
        const doubleQuotes = (firstLines.match(/"/g) || []).length;
        const singleQuotes = (firstLines.match(/'/g) || []).length;
        
        if (singleQuotes > doubleQuotes && singleQuotes > 2) {
          quoteChar = "'";
        }
        
        resolve({ encoding, delimiter, quoteChar });
      };
      
      reader.onerror = () => {
        resolve({ encoding: 'UTF-8', delimiter: ';', quoteChar: '"' });
      };
      
      const slice = file.slice(0, 50 * 1024);
      reader.readAsArrayBuffer(slice);
    });
  };

  const applySelectedContactsFile = useCallback(async (file: File | null) => {
    if (!file) return;
    setContactsDropActive(false);
    setError(null);
    setPreviewError(null);
    setPreviewRawRows([]);
    setPreviewTotalRows(0);
    setPreviewTruncated(false);
    setPreviewHeaders([]);
    setPreviewRows([]);
    setPreviewIgnoredColumnIndexes([]);
    setFileName(file.name);
    
    const isExcel = file.name.endsWith('.xlsx') || file.name.endsWith('.xls');
    if (!isExcel) {
      setIsDetectingCsv(true);
      try {
        const detected = await detectCsvSettings(file);
        setCsvEncoding(detected.encoding);
        setCsvDelimiter(detected.delimiter);
        setCsvStringDelimiter(detected.quoteChar);
      } catch (err) {
        console.warn('Failed to auto-detect CSV settings:', err);
      } finally {
        setIsDetectingCsv(false);
      }
    }
    setFileData(file);
  }, []);

  const handleFileSelect = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] || null;
    await applySelectedContactsFile(file);
    event.target.value = '';
  };

  const processFile = async () => {
    if (!fileData) {
      setError(t('pages:campaigns.create.errors.selectFile'));
      return;
    }

    setError(null);

    try {
      if (previewRawRows.length === 0) {
        throw new Error(t('pages:campaigns.create.errors.noData'));
      }

      const { headers } = structureData(previewRawRows, hasHeaderRow, columnNames);
      
      setColumns(headers);
      
      const phoneKeywords = ['phone', 'telefone', 'numero', 'número', 'celular', 'mobile', 'fone', 'tel', 'whatsapp'];
      const detectedPhoneCols = headers.filter(h => 
        phoneKeywords.some(kw => h.toLowerCase().includes(kw))
      );
      
      if (detectedPhoneCols.length > 0) {
        setPhoneColumns(detectedPhoneCols);
      }

      const otherColumns = headers.filter(h => !detectedPhoneCols.includes(h));
      setVariableColumns(otherColumns);
      
      const namePattern = /name|nome|nm/i;
      const detectedNameCol = headers.find(h => namePattern.test(h));
      if (detectedNameCol) {
        setDisplayNameColumn(detectedNameCol);
      } else {
        setDisplayNameColumn(headers[0] || null);
      }
      setActiveStep(STEP_CONFIGURE_COLUMNS);
    } catch (err) {
      setError(formatPreviewError(err));
    }
  };

  const processContacts = () => {
    if (phoneColumns.length === 0) {
      setError(t('pages:campaigns.create.errors.selectPhoneColumn'));
      return;
    }
    
    if (!displayNameColumn) {
      setError(t('pages:campaigns.create.errors.selectDisplayNameColumn'));
      return;
    }

    if (importedRowsCount === 0) {
      setError(t('pages:campaigns.create.errors.noData'));
      return;
    }

    if (missingRequiredDynamicVariables.length > 0) {
      setError(t('pages:campaigns.create.errors.requiredDynamicVariablesMissing', {
        variables: missingRequiredDynamicVariables.join(', '),
      }));
      return;
    }

    setActiveStep(
      hasAutomationStep
        ? STEP_AUTOMATION
        : hasTransferStep
          ? STEP_TRANSFERS
          : STEP_CAMPAIGN_SETTINGS
    );
  };

  // ============================================================================
  // Agent Management
  // ============================================================================

  const handleScriptToggle = (scriptId: string) => {
    const targetScript = scripts.find((script) => script.id === scriptId);
    setSelectedScripts((previous) => {
      if (previous.includes(scriptId)) {
        return previous.filter((id) => id !== scriptId);
      }
      if (targetScript?.eventType === 'pre_call') {
        const preCallIds = new Set(
          scripts
            .filter((script) => script.eventType === 'pre_call')
            .map((script) => script.id),
        );
        return [...previous.filter((id) => !preCallIds.has(id)), scriptId];
      }
      return [...previous, scriptId];
    });
  };

  // ============================================================================
  // Submit
  // ============================================================================

  const extractMissingRequiredDynamicVariablesFromError = (payload: unknown): string[] => {
    if (!payload || typeof payload !== 'object') {
      return [];
    }
    const data = payload as Record<string, unknown>;

    const collectFromPayload = (value: unknown): string[] => {
      if (!value || typeof value !== 'object') {
        return [];
      }
      const record = value as Record<string, unknown>;
      const direct = record.missingRequiredDynamicVariables;
      if (Array.isArray(direct)) {
        return direct.map((item) => String(item || '').trim()).filter(Boolean);
      }
      return [];
    };

    const directMissing = collectFromPayload(data);
    if (directMissing.length > 0) {
      return directMissing;
    }

    const fileMissing = collectFromPayload(data.contactsFileId);
    if (fileMissing.length > 0) {
      return fileMissing;
    }

    const contactsPayload = data.contacts;
    if (!contactsPayload || typeof contactsPayload !== 'object') {
      return [];
    }

    const missing = new Set<string>();
    Object.values(contactsPayload as Record<string, unknown>).forEach((rowPayload) => {
      collectFromPayload(rowPayload).forEach((variable) => missing.add(variable));
    });
    return Array.from(missing);
  };

  const extractCampaignCreateErrorMessage = (payload: unknown): string | null => {
    if (!payload || typeof payload !== 'object') {
      return null;
    }

    /**
     * Flattens DRF field-level error payloads after axios key transformation.
     */
    const collectMessages = (value: unknown): string[] => {
      if (typeof value === 'string') {
        const message = value.trim();
        return message ? [message] : [];
      }
      if (Array.isArray(value)) {
        return value.flatMap((item) => collectMessages(item));
      }
      if (!value || typeof value !== 'object') {
        return [];
      }

      const nested = value as Record<string, unknown>;
      const directNestedMessage = collectMessages(nested.message);
      if (directNestedMessage.length > 0) {
        return directNestedMessage;
      }
      return Object.values(nested).flatMap((item) => collectMessages(item));
    };

    const data = payload as Record<string, unknown>;
    const directMessage = [data.detail, data.error, data.message].find(
      (entry): entry is string => typeof entry === 'string' && entry.trim().length > 0,
    );
    if (directMessage) {
      return directMessage;
    }

    const filePayload = data.contactsFileId;
    if (filePayload && typeof filePayload === 'object') {
      const fileMessage = (filePayload as Record<string, unknown>).message;
      if (typeof fileMessage === 'string' && fileMessage.trim().length > 0) {
        return fileMessage;
      }
    }

    const sourceAliasesError = data.sourceColumnAliases;
    if (typeof sourceAliasesError === 'string' && sourceAliasesError.trim().length > 0) {
      return sourceAliasesError;
    }

    return Object.values(data)
      .flatMap((entry) => collectMessages(entry))
      .find((message) => message.length > 0) || null;
  };

  const handleSubmit = async () => {
    if (!name) {
      setError(t('pages:campaigns.create.errors.nameRequired'));
      return;
    }
    if (isAiCampaign && !selectedProviderId) {
      setError(t('pages:campaigns.create.errors.providerRequired'));
      return;
    }
    if (isAiCampaign && !selectedAgentId) {
      setError(t('pages:campaigns.create.errors.selectAgent'));
      return;
    }
    if (isAiCampaign && hasSelectedAgentRequiringAppointmentCalendar && !selectedAppointmentCalendarId) {
      setError(t('pages:campaigns.create.errors.appointmentCalendarRequired'));
      return;
    }
    if (isReverseIvrCampaign && !selectedSipTrunkPoolId) {
      setError(t('pages:campaigns.create.errors.sipTrunkPoolRequired'));
      return;
    }
    if (isReverseIvrCampaign && !hasSelectedReverseIvrAudio) {
      setError(t('pages:campaigns.create.errors.ivrAudioRequired'));
      return;
    }
    if (
      isReverseIvrCampaign &&
      ivrAudioDurationSeconds !== null &&
      ivrAudioDurationSeconds > ivrMaxAudioSeconds
    ) {
      setError(
        t('pages:campaigns.create.errors.ivrAudioTooLong', {
          maxSeconds: ivrMaxAudioSeconds,
        }),
      );
      return;
    }
    if (isReverseIvrCampaign) {
      const ivrDtmfErrorKey = ivrDtmfValidationErrorKey;
      if (ivrDtmfErrorKey) {
        setError(t(ivrDtmfErrorKey));
        return;
      }
    }
    if (importedRowsCount === 0) {
      setError(t('pages:campaigns.create.errors.noContacts'));
      return;
    }
    if (missingRequiredDynamicVariables.length > 0) {
      setError(t('pages:campaigns.create.errors.requiredDynamicVariablesMissing', {
        variables: missingRequiredDynamicVariables.join(', '),
      }));
      return;
    }
    const { error: emailsValidationError } = validateNotificationEmails(notificationEmailsInput);
    if (emailsValidationError) {
      setNotificationEmailsError(emailsValidationError);
      return;
    }

    setLoading(true);
    setError(null);
    setUploadProgress(null);
    setSubmitPhase(null);

    try {
      let contactsFileId: string | null = null;
      let ivrAudioFileId: string | null = null;
      let ivrDtmfMapPayload: Record<string, { status: 'converted' | 'optout' | 'mismatch'; label: string | null }> = {};

      if (isReverseIvrCampaign) {
        ivrDtmfMapPayload = buildIvrDtmfMapPayload(ivrDtmfRules);
        if (reverseIvrUseNewAudio && ivrAudioFile) {
          setSubmitPhase('uploading');
          setUploadProgress(null);

          const audioFormData = new FormData();
          audioFormData.append('file', ivrAudioFile);
          audioFormData.append('file_type', 'reverse_ivr_audio');
          if (ivrAudioDisplayName?.trim()) {
            audioFormData.append('display_name', ivrAudioDisplayName.trim());
          }
          if (ivrAudioDurationSeconds !== null) {
            audioFormData.append(
              'metadata',
              JSON.stringify({ duration_seconds: Number(ivrAudioDurationSeconds.toFixed(3)) }),
            );
          }

          const audioUploadResponse = await api.post('/api/media/upload/', audioFormData, {
            headers: { 'Content-Type': 'multipart/form-data' },
          });
          ivrAudioFileId = audioUploadResponse.data.id;
        } else {
          ivrAudioFileId = selectedReverseIvrAudioId || null;
        }
      }
      
      if (fileData) {
        setSubmitPhase('uploading');
        setUploadProgress(0);

        contactsFileId = await uploadMediaFileDirect({
          file: fileData,
          fileType: 'contacts',
          onUploadProgress: (percentCompleted) => {
            setUploadProgress(percentCompleted);
          },
        });
      }
      
      setSubmitPhase('creating');
      setUploadProgress(null);

      const payload: Record<string, unknown> = {
        name,
        description,
        campaignType: selectedCampaignType,
        maxConcurrentCalls: maxConcurrentCalls,
        maxRetries: maxRetries,
        retryDelayMinSeconds: retryDelayMinSeconds,
        retryDelayMaxSeconds: retryDelayMaxSeconds,
        schedule: scheduleEntries.length > 0 ? scheduleEntries : [],
        notificationEmails: notificationEmails,
        automaticConversionEmailsEnabled,
        phoneColumns: phoneColumns,
        variableColumns: variableColumns.filter(c => !phoneColumns.includes(c)),
        displayNameColumn: displayNameColumn,
        sourceColumnAliases: sourceColumnAliases,
        
        removeDuplicates: true,
        removeInvalidPhones: true,
        removeContactsWithoutPhones: true,
        removePreviouslyConvertedContacts,
        removePreviouslyReachedContacts,
        removePreviouslyOptoutContacts,
        defaultCountryCode: defaultCountryCode.toUpperCase(),
        phoneTypeFilter: phoneTypeFilter,
      };

      if (isAiCampaign) {
        payload.aiAgentProviderId = selectedProviderId;
        payload.aiAgentId = selectedAgentId;
        const selectedPreCallScript = scripts.find(
          (script) => script.eventType === 'pre_call' && selectedScripts.includes(script.id),
        );
        if (selectedPreCallScript) {
          payload.preCallScriptId = selectedPreCallScript.id;
        }
        if (hasSelectedAgentRequiringAppointmentCalendar && selectedAppointmentCalendarId) {
          payload.appointmentCalendarId = selectedAppointmentCalendarId;
        }
        if (hasTransferStep && transferTargetType && transferTargetId) {
          payload.transferTargetType = transferTargetType;
          payload.transferTargetId = transferTargetId;
          payload.suspendWhenNoTransferOperatorAvailable = suspendWhenNoTransferOperatorAvailable;
        }
      } else {
        payload.sipTrunkPoolId = selectedSipTrunkPoolId;
        payload.ivrAudioFileId = ivrAudioFileId;
        payload.ivrDtmfMap = ivrDtmfMapPayload;
      }
      
      if (contactsFileId) {
        payload.contactsFileId = contactsFileId;
        payload.fileEncoding = csvEncoding;
        payload.fileDelimiter = csvDelimiter;
        payload.fileTextDelimiter = csvStringDelimiter;
      }

      const campaignResponse = await api.post('/api/campaigns/', payload);
      const campaignId = campaignResponse.data.id;
      
      if (selectedScripts.length > 0) {
        setSubmitPhase('configuring');
        await api.post(`/api/campaigns/${campaignId}/scripts/`, {
          scriptIds: selectedScripts,
        });
      }
      
      navigate(`/campaigns/${campaignId}?showContacts=true`);
    } catch (err: unknown) {
      const error = err as { response?: { data?: unknown } };
      const missingVariables = extractMissingRequiredDynamicVariablesFromError(error?.response?.data);
      if (missingVariables.length > 0) {
        setError(t('pages:campaigns.create.errors.requiredDynamicVariablesMissing', {
          variables: missingVariables.join(', '),
        }));
      } else {
        setError(
          extractCampaignCreateErrorMessage(error?.response?.data) ||
            t('pages:campaigns.create.errors.submitFailed')
        );
      }
    } finally {
      setLoading(false);
      setUploadProgress(null);
      setSubmitPhase(null);
    }
  };

  const isExcelFile = fileName.endsWith('.xlsx') || fileName.endsWith('.xls');

  // ============================================================================
  // Schedule Validation
  // ============================================================================

  // Helper to convert HH:mm to minutes for comparison
  const timeToMinutes = (time: string): number => {
    if (!time) return 0;
    const [h, m] = time.split(':').map(Number);
    return h * 60 + m;
  };

  // Validate schedule entries
  const validateSchedule = useCallback((): ScheduleError[] => {
    const errors: ScheduleError[] = [];
    
    if (scheduleEntries.length === 0) return errors;
    
    // Collect all time ranges per day for overlap checking
    const dayRanges: Record<string, { entryIndex: number; start: number; end: number; period: string }[]> = {};
    
    scheduleEntries.forEach((entry, entryIndex) => {
      // Check if days are selected
      if (entry.days.length === 0) {
        errors.push({
          entryIndex,
          type: 'no_days',
          message: t('pages:campaigns.create.settings.scheduleNoDays'),
        });
        return;
      }
      
      const isAllDay = Boolean(entry.allDay);
      const startMinutes = isAllDay ? 0 : timeToMinutes(entry.start || '');
      const endMinutes = isAllDay ? 24 * 60 : timeToMinutes(entry.end || '');

      // Check if start/end times are valid
      if (!isAllDay && (!entry.start || !entry.end)) {
        return; // Skip if times not filled yet
      }
      
      // Check if end time is after start time
      if (!isAllDay && endMinutes <= startMinutes) {
        errors.push({
          entryIndex,
          type: 'time_invalid',
          message: t('pages:campaigns.create.settings.scheduleTimeInvalid'),
        });
        return;
      }
      
      // Check each day against global limits
      for (const day of entry.days) {
        const globalDay = tenantOperatingHours[day];
        if (!globalDay?.enabled) {
          errors.push({
            entryIndex,
            type: 'outside_limits',
            message: t('pages:campaigns.create.settings.scheduleTenantClosed', {
              day: t(`common:days.${day}`),
            }),
          });
          continue;
        }

        if (isAllDay) {
          if (!globalDay.allDay) {
            errors.push({
              entryIndex,
              type: 'outside_limits',
              message: t('pages:campaigns.create.settings.scheduleAllDayOutsideLimits', {
                day: t(`common:days.${day}`),
              }),
            });
          }
        } else if (!globalDay.allDay && globalDay.start && globalDay.end) {
          const globalStartMinutes = timeToMinutes(globalDay.start);
          const globalEndMinutes = timeToMinutes(globalDay.end);

          // Check if campaign schedule is within global limits
          if (startMinutes < globalStartMinutes || endMinutes > globalEndMinutes) {
            errors.push({
              entryIndex,
              type: 'outside_limits',
              message: t('pages:campaigns.create.settings.scheduleOutsideLimits', {
                day: t(`common:days.${day}`),
                start: globalDay.start,
                end: globalDay.end,
              }),
            });
          }
        }
        
        // Collect for overlap checking
        if (!dayRanges[day]) dayRanges[day] = [];
        dayRanges[day].push({
          entryIndex,
          start: startMinutes,
          end: endMinutes,
          period: isAllDay
            ? t('pages:campaigns.create.settings.scheduleAllDayShort')
            : `${entry.start}-${entry.end}`,
        });
      }
    });
    
    // Check for overlaps between different entries on the same day
    Object.entries(dayRanges).forEach(([day, ranges]) => {
      for (let i = 0; i < ranges.length; i++) {
        for (let j = i + 1; j < ranges.length; j++) {
          const r1 = ranges[i];
          const r2 = ranges[j];
          
          // Check if ranges overlap
          if (r1.start < r2.end && r2.start < r1.end) {
            errors.push({
              entryIndex: r2.entryIndex,
              type: 'overlap',
              message: t('pages:campaigns.create.settings.scheduleOverlap', {
                day: t(`common:days.${day}`),
                period1: r1.period,
                period2: r2.period,
              }),
            });
          }
        }
      }
    });
    
    return errors;
  }, [scheduleEntries, tenantOperatingHours, t]);

  // Update schedule errors whenever entries change
  useEffect(() => {
    setScheduleErrors(validateSchedule());
  }, [validateSchedule]);

  // Get min/max times for a given entry based on selected days
  const getTimeConstraints = (entry: ScheduleEntry): { minTime: string | null; maxTime: string | null } => {
    if (entry.allDay) return { minTime: null, maxTime: null };
    if (entry.days.length === 0) return { minTime: null, maxTime: null };
    
    let minTime: string | null = null;
    let maxTime: string | null = null;
    
    // Find the most restrictive limits across all selected days
    for (const day of entry.days) {
      const globalDay = tenantOperatingHours[day];
      if (!globalDay?.enabled || globalDay.allDay || !globalDay.start || !globalDay.end) continue;
      
      if (!minTime || timeToMinutes(globalDay.start) > timeToMinutes(minTime)) {
        minTime = globalDay.start;
      }
      if (!maxTime || timeToMinutes(globalDay.end) < timeToMinutes(maxTime)) {
        maxTime = globalDay.end;
      }
    }
    
    return { minTime, maxTime };
  };

  // Get errors for a specific entry
  const getEntryErrors = (entryIndex: number): ScheduleError[] => {
    return scheduleErrors.filter(e => e.entryIndex === entryIndex);
  };

  // Generate default schedule entries from tenant operating hours
  const generateDefaultScheduleEntries = useCallback(
    (): ScheduleEntry[] => buildScheduleEntriesFromOperatingHours(tenantOperatingHours),
    [tenantOperatingHours]
  );

  // Reset schedule to tenant defaults
  const resetScheduleToDefaults = useCallback(() => {
    setScheduleEntries(generateDefaultScheduleEntries());
  }, [generateDefaultScheduleEntries]);

  // ============================================================================
  // Step Validation
  // ============================================================================

  const canProceedFromStep = (step: number): boolean => {
    const ivrDtmfErrorKey = ivrDtmfValidationErrorKey;
    switch (step) {
      case STEP_BASIC_INFO:
        if (!hasAvailableCampaignTypes) {
          return false;
        }
        if (!name) {
          return false;
        }
        if (isAiCampaign) {
          return !!selectedProviderId;
        }
        return !!selectedSipTrunkPoolId && hasSelectedReverseIvrAudio && !ivrDtmfErrorKey;
      case STEP_AGENTS:
        return Boolean(selectedAgentId)
          && (
            !hasSelectedAgentRequiringAppointmentCalendar
            || Boolean(selectedAppointmentCalendarId)
          );
      case STEP_UPLOAD_CONTACTS:
        return !!fileData && !previewLoading && previewHeaders.length > 0 && previewRows.length > 0;
      case STEP_CONFIGURE_COLUMNS:
        return phoneColumns.length > 0 && !!displayNameColumn && missingRequiredDynamicVariables.length === 0;
      case STEP_AUTOMATION:
        return !hasSelectedAgentRequiringPreCall || scripts.some(
          (script) => script.eventType === 'pre_call' && selectedScripts.includes(script.id),
        );
      case STEP_TRANSFERS:
        if (!hasTransferStep) {
          return true;
        }
        return !!transferTargetType && !!transferTargetId;
      case STEP_CAMPAIGN_SETTINGS:
        // Require at least one schedule entry and no validation errors
        return scheduleEntries.length > 0 && scheduleErrors.length === 0 && !notificationEmailsError;
      default:
        return false;
    }
  };

  // ============================================================================
  // Render Helpers
  // ============================================================================

  const renderSettingsSection = (
    title: string,
    icon: React.ReactNode,
    children: React.ReactNode
  ) => (
    <Paper
      sx={{
        p: 3,
        mb: 3,
        bgcolor: theme.palette.mode === 'light' ? theme.palette.grey[200] : 'transparent',
      }}
      variant="outlined"
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
        {icon}
        <Typography variant="h6" fontWeight={600}>{title}</Typography>
      </Box>
      {children}
    </Paper>
  );

  // ============================================================================
  // Render
  // ============================================================================

  const breadcrumbs = [
    { label: t('common:menu.campaigns'), to: '/campaigns' },
    { label: t('pages:campaigns.create.title') },
  ];

  return (
    <Box>
      <DetailHeader
        title={t('pages:campaigns.create.title')}
        subtitle={t('pages:campaigns.create.subtitle')}
        sectionIcon={<CampaignIcon sx={{ fontSize: 32, color: 'primary.main' }} />}
        breadcrumbs={breadcrumbs}
        backHref="/campaigns"
      />
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      <Stepper activeStep={activeStepperIndex} sx={{ mb: 4 }} alternativeLabel>
        {visibleSteps.map((step, index) => (
          <Step key={step.label}>
            <StepLabel
              StepIconComponent={() => (
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: index <= activeStepperIndex ? 'primary.main' : 'action.disabledBackground',
                    color: index <= activeStepperIndex ? 'primary.contrastText' : 'action.disabled',
                    transition: 'all 0.3s',
                  }}
                >
                  {index < activeStepperIndex ? <CheckCircle /> : step.icon}
                </Box>
              )}
            >
              {step.label}
            </StepLabel>
          </Step>
        ))}
      </Stepper>
      {loadingData && (
        <LinearProgress sx={{ mb: 2 }} />
      )}
      {/* Step 1: Basic Info */}
      {activeStep === STEP_BASIC_INFO && (
        <Card>
          <CardContent sx={{ p: 4 }}>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Info color="primary" /> {t('pages:campaigns.create.basicInfo.title')}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              {t('pages:campaigns.create.basicInfo.description')}
            </Typography>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              <TextField
                fullWidth
                label={`${t('pages:campaigns.create.basicInfo.campaignName')} *`}
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={t('pages:campaigns.create.basicInfo.campaignNamePlaceholder')}
              />

              <TextField
                fullWidth
                label={t('pages:campaigns.create.basicInfo.descriptionLabel')}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                multiline
                rows={3}
                placeholder={t('pages:campaigns.create.basicInfo.descriptionPlaceholder')}
              />

              {isCampaignTypeSelectable ? (
                <FormControl fullWidth>
                  <InputLabel>{t('pages:campaigns.create.basicInfo.campaignType')} *</InputLabel>
                  <Select
                    value={selectedCampaignType}
                    label={`${t('pages:campaigns.create.basicInfo.campaignType')} *`}
                    onChange={(event) => setSelectedCampaignType(event.target.value as CampaignCreationType)}
                  >
                    {availableCampaignTypes.includes('ai') && (
                      <MenuItem value="ai">{t('pages:campaigns.create.basicInfo.campaignTypeAi')}</MenuItem>
                    )}
                    {availableCampaignTypes.includes('reverse_ivr') && (
                      <MenuItem value="reverse_ivr">{t('pages:campaigns.create.basicInfo.campaignTypeReverseIvr')}</MenuItem>
                    )}
                  </Select>
                </FormControl>
              ) : availableCampaignTypes.length === 1 ? null : (
                <Alert severity="error">
                  {t('pages:campaigns.create.basicInfo.noCampaignTypeAvailable')}
                </Alert>
              )}

              {hasAvailableCampaignTypes && isAiCampaign && (
                <>
                  {/* Provider Selection - only show if more than one provider */}
                  {phoneProviders.length > 1 && (
                    <FormControl fullWidth>
                      <InputLabel>{t('pages:campaigns.create.basicInfo.ai_agent_provider')} *</InputLabel>
                      <Select
                        value={selectedProviderId || ''}
                        label={`${t('pages:campaigns.create.basicInfo.ai_agent_provider')} *`}
                        onChange={(e) => {
                          setSelectedProviderId(e.target.value as string);
                          // Reset agents when provider changes
                          setSelectedAgentId('');
                          setAgents([]);
                        }}
                      >
                        {phoneProviders.map(provider => (
                          <MenuItem key={provider.id} value={provider.id}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Business fontSize="small" />
                              <span>{provider.name}</span>
                              <Chip
                                label={PROVIDER_TYPE_LABELS[provider.providerType] || provider.providerType}
                                size="small"
                                sx={{ height: 20, fontSize: '0.75rem' }}
                              />
                            </Box>
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}

                  {/* Show selected provider info if only one available */}
                  {phoneProviders.length === 1 && selectedProvider && (
                    <Paper sx={{ p: 2, bgcolor: alpha('#4caf50', 0.1), border: '1px solid', borderColor: 'success.main' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <CheckCircle color="success" />
                        <Box>
                          <Typography variant="body2" color="text.secondary">{t('pages:campaigns.create.basicInfo.providerSelected')}</Typography>
                          <Typography fontWeight={600}>{selectedProvider.name}</Typography>
                        </Box>
                      </Box>
                    </Paper>
                  )}

                  {phoneProviders.length === 0 && !loadingData && (
                    <Alert severity="warning">
                      {t('pages:campaigns.create.basicInfo.noProviders')}
                    </Alert>
                  )}
                </>
              )}

              {hasAvailableCampaignTypes && isReverseIvrCampaign && (
                <>
                  <FormControl fullWidth>
                    <InputLabel>{t('pages:campaigns.create.basicInfo.sipTrunkPool')} *</InputLabel>
                    <Select
                      value={selectedSipTrunkPoolId}
                      label={`${t('pages:campaigns.create.basicInfo.sipTrunkPool')} *`}
                      onChange={(event) => setSelectedSipTrunkPoolId(event.target.value)}
                    >
                      {sipTrunkPools.map((pool) => (
                        <MenuItem key={pool.id} value={pool.id}>
                          {pool.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  {!selectedSipTrunkPool && sipTrunkPools.length === 0 && (
                    <Alert severity="warning">
                      {t('pages:campaigns.create.basicInfo.noSipTrunkPools')}
                    </Alert>
                  )}

                  <Paper sx={{ p: 2, border: '1px dashed', borderColor: 'divider' }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                      <Typography variant="subtitle2">
                        {t('pages:campaigns.create.basicInfo.ivrAudio')} *
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {t('pages:campaigns.create.basicInfo.ivrAudioHelp', { maxSeconds: ivrMaxAudioSeconds })}
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                        <Autocomplete
                          fullWidth
                          options={reverseIvrAudioOptions}
                          value={selectedReverseIvrAudioOption}
                          disabled={reverseIvrUseNewAudio}
                          onChange={(_, option) => {
                            setError(null);
                            setIvrAudioError(null);
                            setSelectedReverseIvrAudioId(option?.id ?? '');
                            setSelectedReverseIvrAudioPreviewUrl(null);
                          }}
                          getOptionLabel={(option) => option.name || option.id}
                          isOptionEqualToValue={(option, value) => option.id === value.id}
                          noOptionsText={t('pages:campaigns.create.basicInfo.ivrAudioNoExisting', 'Nenhum áudio disponível')}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label={t('pages:campaigns.create.basicInfo.ivrAudioExisting', 'Áudio existente')}
                              placeholder={t('pages:campaigns.create.basicInfo.ivrAudioExistingPlaceholder', 'Selecionar áudio')}
                            />
                          )}
                          sx={{ flex: '1 1 480px', minWidth: 280 }}
                        />
                        <FormControlLabel
                          control={(
                            <Switch
                              checked={reverseIvrUseNewAudio}
                              onChange={(_, checked) => {
                                setReverseIvrUseNewAudio(checked);
                                if (!checked && reverseIvrAudioOptions.length === 1) {
                                  setSelectedReverseIvrAudioId(reverseIvrAudioOptions[0].id);
                                }
                              }}
                            />
                          )}
                          label={t('pages:campaigns.create.basicInfo.ivrAudioNewToggle', 'Novo')}
                          sx={{ m: 0 }}
                        />
                      </Box>
                      {!reverseIvrUseNewAudio && selectedReverseIvrAudioId && reverseIvrAudioPreviewLoading && (
                        <Typography variant="caption" color="text.secondary">
                          {t('pages:campaigns.create.basicInfo.ivrAudioPreviewLoading', 'Carregando preview do áudio...')}
                        </Typography>
                      )}
                      {!reverseIvrUseNewAudio && selectedReverseIvrAudioPreviewUrl && (
                        <WaveformPlayer
                          url={selectedReverseIvrAudioPreviewUrl}
                          displayName={getReverseIvrAudioDisplayName(selectedReverseIvrAudioOption)}
                          downloadFilename={getReverseIvrAudioDownloadFilename(selectedReverseIvrAudioOption)}
                          durationSeconds={selectedReverseIvrAudioOption?.durationSeconds ?? null}
                          displayNameEditable
                          displayNameSaving={reverseIvrAudioFilenameSaving}
                          onDisplayNameCommit={handleIvrAudioDisplayNameCommit}
                        />
                      )}
                      {!reverseIvrUseNewAudio
                        && selectedReverseIvrAudioId
                        && !reverseIvrAudioPreviewLoading
                        && !selectedReverseIvrAudioPreviewUrl && (
                        <Alert severity="info">
                          {t(
                            'pages:campaigns.create.basicInfo.ivrAudioPreviewUnavailable',
                            'Não foi possível carregar o preview do áudio selecionado.',
                          )}
                        </Alert>
                      )}
                      {reverseIvrUseNewAudio && (
                        <WaveformPlayerField
                          url={ivrAudioPreviewUrl}
                          displayName={ivrAudioDisplayName || ivrAudioFile?.name}
                          downloadFilename={ivrAudioFile?.name || undefined}
                          durationSeconds={ivrAudioDurationSeconds}
                          displayNameEditable
                          uploadEnabled
                          displayNameSaving={reverseIvrAudioFilenameSaving}
                          onDisplayNameCommit={handleIvrAudioDisplayNameCommit}
                          onSelectFile={handleIvrAudioSelect}
                          accept={IVR_AUDIO_FILE_ACCEPT}
                          onRemove={() => {
                            if (ivrAudioPreviewUrl) {
                              URL.revokeObjectURL(ivrAudioPreviewUrl);
                            }
                            setIvrAudioFile(null);
                            setIvrAudioDisplayName(null);
                            setIvrAudioDurationSeconds(null);
                            setIvrAudioPreviewUrl(null);
                            setIvrAudioError(null);
                          }}
                          constraints={{
                            maxDurationSeconds: ivrMaxAudioSeconds,
                            maxFileSizeBytes: ivrMaxAudioFileSizeBytes || undefined,
                          }}
                          showConstraints
                        />
                      )}
                      {ivrAudioError && (
                        <Alert severity="error">
                          {ivrAudioError}
                        </Alert>
                      )}
                    </Box>
                  </Paper>

                  <ReverseIvrDtmfEditor
                    rules={ivrDtmfRules}
                    onChange={setIvrDtmfRules}
                    required
                  />
                </>
              )}
            </Box>

            <Box sx={{ mt: 4, display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                variant="contained"
                endIcon={<ArrowForward />}
                onClick={() => setActiveStep(isAiCampaign ? STEP_AGENTS : STEP_UPLOAD_CONTACTS)}
                disabled={!canProceedFromStep(STEP_BASIC_INFO)}
              >
                {t('pages:campaigns.create.continue')}
              </Button>
            </Box>
          </CardContent>
        </Card>
      )}
      {/* Step 2: Agents */}
      {isAiCampaign && activeStep === STEP_AGENTS && (
        <Card>
          <CardContent sx={{ p: 4 }}>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <PsychologyIcon color="primary" /> {t('pages:campaigns.create.agents.title')}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              {t('pages:campaigns.create.agents.description')}
              {selectedProvider && (
                <Chip 
                  label={selectedProvider.name}
                  size="small"
                  sx={{ ml: 1 }}
                />
              )}
            </Typography>

            {availableAgents.length === 0 ? (
              <Alert severity="warning">
                {t('pages:campaigns.create.agents.noAgents')}
              </Alert>
            ) : availableAgents.length === 1 ? (
              // Single agent - show as locked
              (<Paper sx={{ p: 3, bgcolor: alpha('#4caf50', 0.1), border: '1px solid', borderColor: 'success.main' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Lock color="success" />
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="body2" color="text.secondary">{t('pages:campaigns.create.agents.agent')}</Typography>
                    <Typography fontWeight={600}>{availableAgents[0].name}</Typography>
                    {availableAgents[0].description && (
                      <Typography variant="caption" color="text.secondary">
                        {availableAgents[0].description}
                      </Typography>
                    )}
                  </Box>
                  <CheckCircle color="success" />
                </Box>
              </Paper>)
            ) : (
              // Multiple agents - show single selection
              (<Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {availableAgents.map(agent => {
                  const isSelected = selectedAgentId === agent.id;
                  return (
                    <Paper
                      key={agent.id}
                      sx={{
                        p: 2,
                        cursor: 'pointer',
                        border: '2px solid',
                        borderColor: isSelected ? 'secondary.main' : 'divider',
                        bgcolor: isSelected ? alpha('#9c27b0', 0.05) : 'background.paper',
                        '&:hover': { borderColor: 'secondary.main' },
                        transition: 'all 0.2s',
                      }}
                      onClick={() => setSelectedAgentId(agent.id)}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Checkbox checked={isSelected} color="secondary" />
                        <PsychologyIcon color={isSelected ? 'secondary' : 'action'} />
                        <Box sx={{ flex: 1 }}>
                          <Typography fontWeight={600}>{agent.name}</Typography>
                          {agent.description && (
                            <Typography variant="caption" color="text.secondary">
                              {agent.description}
                            </Typography>
                          )}
                        </Box>
                        {isSelected && <CheckCircle color="secondary" />}
                      </Box>
                    </Paper>
                  );
                })}
              </Box>)
            )}

            {selectedAgent && (
              <Paper
                variant="outlined"
                sx={{
                  mt: 3,
                  p: 2.5,
                  bgcolor: theme.palette.mode === 'light' ? theme.palette.grey[200] : 'transparent',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                  <Hub color="secondary" fontSize="small" />
                  <Typography variant="subtitle2">
                    {t('pages:campaigns.create.agents.requiredDynamicVariablesTitle')}
                  </Typography>
                </Box>
                {selectedAgentRequiredVariables.length === 0 && selectedAgentOptionalVariables.length === 0 ? (
                  <Alert severity="info" icon={<Info />}>
                    {t('pages:campaigns.create.agents.requiredDynamicVariablesEmpty')}
                  </Alert>
                ) : (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    <Typography variant="body2" color="text.secondary">
                      {t('pages:campaigns.create.agents.requiredDynamicVariablesHelp')}
                    </Typography>
                    {selectedAgentRequiredVariables.length > 0 ? (
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <Typography variant="caption" color="text.secondary">
                          {t('pages:campaigns.create.agents.requiredVariablesLabel')}
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                          {selectedAgentRequiredVariables.map((variable) => (
                            <Chip
                              key={variable}
                              label={variable}
                              color="secondary"
                              variant="outlined"
                              size="small"
                            />
                          ))}
                        </Box>
                      </Box>
                    ) : (
                      <Alert severity="info" icon={<Info />}>
                        {t('pages:campaigns.create.agents.requiredDynamicVariablesEmpty')}
                      </Alert>
                    )}
                    {selectedAgentOptionalVariables.length > 0 && (
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <Typography variant="caption" color="text.secondary">
                          {t('pages:campaigns.create.agents.optionalDynamicVariablesHelp')}
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                          {selectedAgentOptionalVariables.map((variable) => (
                            <Chip
                              key={variable}
                              label={variable}
                              color="default"
                              variant="outlined"
                              size="small"
                            />
                          ))}
                        </Box>
                      </Box>
                    )}
                  </Box>
                )}
              </Paper>
            )}

            {hasSelectedAgentRequiringAppointmentCalendar && (
              <Paper
                variant="outlined"
                sx={{
                  mt: 3,
                  p: 2.5,
                  bgcolor: theme.palette.mode === 'light' ? theme.palette.grey[200] : 'transparent',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                  <CalendarMonth color="secondary" fontSize="small" />
                  <Typography variant="subtitle2">
                    {t('pages:campaigns.create.appointments.title')}
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {t('pages:campaigns.create.appointments.description')}
                </Typography>

                {appointmentCalendarsError ? (
                  <Alert severity="error">{appointmentCalendarsError}</Alert>
                ) : appointmentCalendarsLoading ? (
                  <Typography variant="body2" color="text.secondary">
                    {t('pages:campaigns.create.appointments.loading')}
                  </Typography>
                ) : appointmentCalendars.length === 0 ? (
                  <Alert severity="warning">
                    {t('pages:campaigns.create.appointments.empty')}
                  </Alert>
                ) : (
                  <Autocomplete<AppointmentCalendar, false, false, false>
                    fullWidth
                    options={appointmentCalendars}
                    value={selectedAppointmentCalendar}
                    disabled={appointmentCalendars.length === 1}
                    onChange={(_, nextCalendar) => setSelectedAppointmentCalendarId(nextCalendar?.id || '')}
                    getOptionLabel={(option) => option.name}
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    noOptionsText={t('pages:campaigns.create.appointments.empty')}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        required
                        label={t('pages:campaigns.create.appointments.calendar')}
                        helperText={
                          appointmentCalendars.length === 1
                            ? t('pages:campaigns.create.appointments.autoSelected')
                            : t('pages:campaigns.create.appointments.help')
                        }
                      />
                    )}
                  />
                )}
              </Paper>
            )}

            <Box sx={{ mt: 4, display: 'flex', justifyContent: 'space-between' }}>
              <Button startIcon={<ArrowBack />} onClick={() => setActiveStep(STEP_BASIC_INFO)}>
                {t('pages:campaigns.create.back')}
              </Button>
              <Button
                variant="contained"
                endIcon={<ArrowForward />}
                onClick={() => setActiveStep(STEP_UPLOAD_CONTACTS)}
                disabled={!canProceedFromStep(STEP_AGENTS)}
              >
                {t('pages:campaigns.create.continue')}
              </Button>
            </Box>
          </CardContent>
        </Card>
      )}
      {/* Step 4: Upload Contacts */}
      {activeStep === STEP_UPLOAD_CONTACTS && (
        <Card>
          <CardContent sx={{ p: 4 }}>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CloudUpload color="primary" /> {t('pages:campaigns.create.uploadContacts.title')}
            </Typography>
            <ButtonTabs
              tabs={[
                {
                  id: 'select-file',
                  label: t('pages:campaigns.create.uploadContacts.tabs.selectFile'),
                  icon: <CloudUpload />,
                  content: (
                    <>
                      <Paper
                        sx={{
                          border: fileData ? '2px solid' : '2px dashed',
                          borderColor: fileData ? 'success.main' : contactsDropActive ? 'primary.main' : 'divider',
                          borderRadius: 2,
                          p: 4,
                          textAlign: 'center',
                          cursor: 'pointer',
                          bgcolor: fileData ? 'success.main' : contactsDropActive ? 'action.hover' : 'background.default',
                          '&:hover': { bgcolor: 'action.hover' },
                          transition: 'all 0.2s',
                        }}
                        onClick={() => document.getElementById('file-input')?.click()}
                        onDragEnter={(event: ReactDragEvent<HTMLDivElement>) => {
                          event.preventDefault();
                          event.stopPropagation();
                          setContactsDropActive(true);
                        }}
                        onDragLeave={(event: ReactDragEvent<HTMLDivElement>) => {
                          event.preventDefault();
                          event.stopPropagation();
                          setContactsDropActive(false);
                        }}
                        onDragOver={(event: ReactDragEvent<HTMLDivElement>) => {
                          event.preventDefault();
                          event.stopPropagation();
                        }}
                        onDrop={(event: ReactDragEvent<HTMLDivElement>) => {
                          event.preventDefault();
                          event.stopPropagation();
                          setContactsDropActive(false);
                          const droppedFile = event.dataTransfer.files?.[0] || null;
                          void applySelectedContactsFile(droppedFile);
                        }}
                      >
                        {fileData ? (
                          <Box>
                            <CheckCircle sx={{ fontSize: 64, color: 'success.contrastText', mb: 2 }} />
                            <Typography variant="h6" color="success.contrastText">{fileName}</Typography>
                            <Typography variant="body2" color="success.contrastText" sx={{ opacity: 0.8 }}>
                              {t('pages:campaigns.create.uploadContacts.clickToChange')}
                            </Typography>
                          </Box>
                        ) : (
                          <Box>
                            <CloudUpload sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                            <Typography variant="h6">
                              {t('pages:campaigns.create.uploadContacts.dragDrop')}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {t('pages:campaigns.create.uploadContacts.supportedFormats')}
                            </Typography>
                          </Box>
                        )}
                        <input
                          id="file-input"
                          type="file"
                          accept=".csv,.xlsx,.xls,.txt"
                          onChange={handleFileSelect}
                          style={{ display: 'none' }}
                        />
                      </Paper>

                      {fileData && !isExcelFile && (
                        <Paper sx={{ mt: 3, p: 3 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                            <Typography variant="subtitle1" fontWeight={600}>
                              {t('pages:campaigns.create.uploadContacts.csvSettings')}
                            </Typography>
                            {isDetectingCsv ? (
                              <Chip 
                                size="small" 
                                label={t('pages:campaigns.create.uploadContacts.detecting')}
                                color="info" 
                                icon={<Sync className="spin" sx={{ fontSize: 16 }} />}
                              />
                            ) : (
                              <Chip 
                                size="small" 
                                label={t('pages:campaigns.create.uploadContacts.autoDetected')}
                                color="success" 
                                variant="outlined"
                                icon={<CheckCircle sx={{ fontSize: 16 }} />}
                              />
                            )}
                          </Box>
                          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 2 }}>
                            <FormControl fullWidth size="small">
                              <InputLabel>{t('pages:campaigns.create.uploadContacts.encoding')}</InputLabel>
                              <Select
                                value={csvEncoding}
                                label={t('pages:campaigns.create.uploadContacts.encoding')}
                                onChange={(e) => setCsvEncoding(e.target.value)}
                              >
                                <MenuItem value="UTF-8">UTF-8</MenuItem>
                                <MenuItem value="ISO-8859-1">ISO-8859-1 (Latin-1)</MenuItem>
                                <MenuItem value="windows-1252">Windows-1252</MenuItem>
                                <MenuItem value="UTF-16">UTF-16</MenuItem>
                              </Select>
                            </FormControl>
                            <FormControl fullWidth size="small">
                              <InputLabel>{t('pages:campaigns.create.uploadContacts.delimiter')}</InputLabel>
                              <Select
                                value={csvDelimiter}
                                label={t('pages:campaigns.create.uploadContacts.delimiter')}
                                onChange={(e) => setCsvDelimiter(e.target.value)}
                              >
                                <MenuItem value=";">{t('common:semicolon')} ( ; )</MenuItem>
                                <MenuItem value=",">{t('common:comma')} ( , )</MenuItem>
                                <MenuItem value={'\t'}>{t('pages:campaigns.create.uploadContacts.delimiterOptions.tab')}</MenuItem>
                                <MenuItem value="|">{t('pages:campaigns.create.uploadContacts.delimiterOptions.pipe')} ( | )</MenuItem>
                              </Select>
                            </FormControl>
                            <FormControl fullWidth size="small">
                              <InputLabel>{t('pages:campaigns.create.uploadContacts.textDelimiter')}</InputLabel>
                              <Select
                                value={csvStringDelimiter}
                                label={t('pages:campaigns.create.uploadContacts.textDelimiter')}
                                onChange={(e) => setCsvStringDelimiter(e.target.value)}
                              >
                                <MenuItem value='"'>{t('common:doubleQuotes')} ( " )</MenuItem>
                                <MenuItem value="'">{t('common:singleQuotes')} ( ' )</MenuItem>
                                <MenuItem value="">{t('pages:campaigns.create.uploadContacts.textDelimiterOptions.none')}</MenuItem>
                              </Select>
                            </FormControl>
                          </Box>
                          <Alert severity="info" sx={{ mt: 2 }}>
                            {t('pages:campaigns.create.uploadContacts.previewTroubleshooting')}
                          </Alert>
                        </Paper>
                      )}

                      {fileData && (
                        <Paper
                          sx={{
                            mt: 3,
                            p: 3,
                            bgcolor: theme.palette.mode === 'light' ? theme.palette.grey[200] : 'transparent',
                          }}
                          variant="outlined"
                        >
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                              <Typography variant="subtitle1" fontWeight={600} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Description color="primary" /> {t('pages:campaigns.create.uploadContacts.filePreview')}
                              </Typography>
                              {previewRows.length > 0 && (
                                <Chip 
                                  label={t('pages:campaigns.create.uploadContacts.previewSummary', {
                                    rows: formatNumber(previewRows.length),
                                    columns: formatNumber(previewHeaders.length),
                                  })}
                                  size="small"
                                  color="primary"
                                  variant="outlined"
                                />
                              )}
                            </Box>
                            {previewRows.length > 0 && (
                              <Tooltip title={t('common:searchInFile')}>
                                <Button
                                  size="small"
                                  variant={previewShowSearch ? 'contained' : 'outlined'}
                                  onClick={handleTogglePreviewSearch}
                                  startIcon={<Info />}
                                >
                                  {t('pages:campaigns.create.uploadContacts.search')}
                                </Button>
                              </Tooltip>
                            )}
                          </Box>
                          
                          {previewError ? (
                            <Alert severity="error">{previewError}</Alert>
                          ) : previewHeaders.length > 0 ? (
                            <>
                              {previewTruncated && (
                                <Alert severity="info" sx={{ mb: 2 }}>
                                  {t('pages:campaigns.create.uploadContacts.previewLimited', {
                                    shown: formatNumber(previewRows.length),
                                    total: formatNumber(importedRowsCount),
                                  })}
                                </Alert>
                              )}
                              {previewIgnoredColumnsText && (
                                <Alert severity="info" sx={{ mb: 2 }}>
                                  {t('pages:campaigns.create.uploadContacts.blankUnnamedColumnsIgnored', {
                                    columns: previewIgnoredColumnsText,
                                  })}
                                </Alert>
                              )}
                              {previewShowSearch && (
                                <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                                  <TextField
                                    fullWidth
                                    size="small"
                                    autoFocus
                                    placeholder={t('pages:campaigns.create.uploadContacts.searchPlaceholder')}
                                    value={previewSearchTerm}
                                    onChange={(event) => setPreviewSearchTerm(event.target.value)}
                                  />
                                  <Button
                                    variant="text"
                                    onClick={() => setPreviewSearchTerm('')}
                                    disabled={!previewSearchTerm}
                                  >
                                    {t('common:actions.clear')}
                                  </Button>
                                </Box>
                              )}
                              {previewShowSearch && previewSearchTerm.trim() && filteredPreviewRows.length === 0 && (
                                <Alert severity="info" sx={{ mb: 2 }}>
                                  {t('pages:campaigns.create.uploadContacts.searchNoResults')}
                                </Alert>
                              )}
                              <Box
                                sx={{
                                  height: 400,
                                  border: 1,
                                  borderColor: 'divider',
                                  borderRadius: 1,
                                  overflow: 'hidden',
                                  scrollbarWidth: 'thin',
                                  scrollbarColor: `${alpha(theme.palette.text.primary, 0.35)} ${alpha(theme.palette.text.primary, 0.08)}`,
                                  '& ::-webkit-scrollbar': {
                                    height: 10,
                                    width: 10,
                                  },
                                  '& ::-webkit-scrollbar-track': {
                                    backgroundColor: alpha(theme.palette.text.primary, 0.06),
                                    borderRadius: 999,
                                  },
                                  '& ::-webkit-scrollbar-thumb': {
                                    backgroundColor: alpha(theme.palette.text.primary, 0.3),
                                    borderRadius: 999,
                                    border: `2px solid ${alpha(theme.palette.text.primary, 0.06)}`,
                                  },
                                  '& ::-webkit-scrollbar-thumb:hover': {
                                    backgroundColor: alpha(theme.palette.text.primary, 0.45),
                                  },
                                }}
                              >
                                <DataEditor
                                  getCellContent={([col, row]: Item): GridCell => {
                                    const rowData = filteredPreviewRows[row];
                                    if (!rowData) {
                                      return { kind: GridCellKind.Text, data: '', displayData: '', allowOverlay: false };
                                    }
                                    const value = rowData[col] || '';
                                    return {
                                      kind: GridCellKind.Text,
                                      data: value,
                                      displayData: value || '-',
                                      allowOverlay: true,
                                    };
                                  }}
                                  columns={previewHeaders.map((header, idx): GridColumn => ({
                                    title: header || `${t('common:column')} ${idx + 1}`,
                                    width: Math.max(100, Math.min(200, (header?.length || 10) * 10 + 40)),
                                  }))}
                                  rows={filteredPreviewRows.length}
                                  smoothScrollX
                                  smoothScrollY
                                  rowMarkers="number"
                                  getCellsForSelection={true}
                                  width="100%"
                                  height="100%"
                                  theme={dataGridTheme}
                                />
                              </Box>
                            </>
                          ) : (
                            <Box sx={{ textAlign: 'center', py: 4 }}>
                              <LinearProgress />
                              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                {t('pages:campaigns.create.uploadContacts.loadingPreview')}
                              </Typography>
                            </Box>
                          )}
                        </Paper>
                      )}
                    </>
                  ),
                },
                {
                  id: 'validation-rules',
                  label: t('pages:campaigns.create.uploadContacts.tabs.validationRules'),
                  icon: <Build />,
                  content: (
                    <Paper
                      sx={{
                        p: 3,
                        bgcolor: theme.palette.mode === 'light' ? theme.palette.grey[200] : 'transparent',
                      }}
                      variant="outlined"
                    >
                      <Alert severity="info" sx={{ mb: 2 }}>
                        {t('pages:campaigns.create.configureColumns.validationRulesInfo')}
                      </Alert>

                      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr' }, gap: 2 }}>
                        <Paper sx={{ p: 2, bgcolor: alpha('#4caf50', 0.05), border: '1px solid', borderColor: 'info.dark' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                            <Info color="info" sx={{ fontSize: 20 }} />
                            <Typography variant="body2" fontWeight={500}>
                              {t('pages:campaigns.create.configureColumns.removeDuplicates')}
                            </Typography>
                          </Box>
                          <Typography variant="caption" color="text.secondary">
                            {t('pages:campaigns.create.configureColumns.removeDuplicatesHelp')}
                          </Typography>
                        </Paper>

                        <Paper sx={{ p: 2, bgcolor: alpha('#4caf50', 0.05), border: '1px solid', borderColor: 'info.dark' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                            <Info color="info" sx={{ fontSize: 20 }} />
                            <Typography variant="body2" fontWeight={500}>
                              {t('pages:campaigns.create.configureColumns.removeInvalidPhones')}
                            </Typography>
                          </Box>
                          <Typography variant="caption" color="text.secondary">
                            {t('pages:campaigns.create.configureColumns.removeInvalidPhonesHelp')}
                          </Typography>
                        </Paper>

                        <Paper sx={{ p: 2, bgcolor: alpha('#4caf50', 0.05), border: '1px solid', borderColor: 'info.dark' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                            <Info color="info" sx={{ fontSize: 20 }} />
                            <Typography variant="body2" fontWeight={500}>
                              {t('pages:campaigns.create.configureColumns.removeContactsWithoutPhones')}
                            </Typography>
                          </Box>
                          <Typography variant="caption" color="text.secondary">
                            {t('pages:campaigns.create.configureColumns.removeContactsWithoutPhonesHelp')}
                          </Typography>
                        </Paper>
                      </Box>

                      <Paper sx={{ p: 2.5, mt: 3 }} variant="outlined">
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1 }}>
                          <Typography variant="body2" fontWeight={600}>
                            {t('pages:campaigns.create.configureColumns.historicalFiltersTitle')}
                          </Typography>
                          <Tooltip title={t('pages:campaigns.create.configureColumns.historicalFiltersHelp')} arrow>
                            <HelpOutline sx={{ fontSize: 14, color: 'text.secondary', cursor: 'help' }} />
                          </Tooltip>
                        </Box>
                        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr' }, gap: 1.5 }}>
                          <FormControlLabel
                            control={
                              <Switch
                                checked={removePreviouslyConvertedContacts}
                                onChange={(event) => setRemovePreviouslyConvertedContacts(event.target.checked)}
                              />
                            }
                            label={
                              <Box>
                                <Typography variant="body2" fontWeight={500}>
                                  {t('pages:campaigns.create.configureColumns.removePreviouslyConvertedContacts')}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {t('pages:campaigns.create.configureColumns.removePreviouslyConvertedContactsHelp')}
                                </Typography>
                              </Box>
                            }
                          />
                          <FormControlLabel
                            control={
                              <Switch
                                checked={removePreviouslyReachedContacts}
                                onChange={(event) => setRemovePreviouslyReachedContacts(event.target.checked)}
                              />
                            }
                            label={
                              <Box>
                                <Typography variant="body2" fontWeight={500}>
                                  {t('pages:campaigns.create.configureColumns.removePreviouslyReachedContacts')}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {t('pages:campaigns.create.configureColumns.removePreviouslyReachedContactsHelp')}
                                </Typography>
                              </Box>
                            }
                          />
                          <FormControlLabel
                            control={
                              <Switch
                                checked={removePreviouslyOptoutContacts}
                                onChange={(event) => setRemovePreviouslyOptoutContacts(event.target.checked)}
                              />
                            }
                            label={
                              <Box>
                                <Typography variant="body2" fontWeight={500}>
                                  {t('pages:campaigns.create.configureColumns.removePreviouslyOptoutContacts')}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {t('pages:campaigns.create.configureColumns.removePreviouslyOptoutContactsHelp')}
                                </Typography>
                              </Box>
                            }
                          />
                        </Box>
                      </Paper>

                      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2, mt: 3 }}>
                        <Paper sx={{ p: 2 }}>
                          <Typography variant="body2" fontWeight={500} gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Public color="primary" /> {t('pages:campaigns.create.configureColumns.defaultCountryCode')}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
                            {t('pages:campaigns.create.configureColumns.defaultCountryCodeHelp')}
                          </Typography>
                          <CountrySelector
                            selectedCountry={defaultCountryCode}
                            onSelect={(country) => setDefaultCountryCode(country.iso2)}
                            countries={defaultCountries}
                            renderButtonWrapper={({ rootProps }) => {
                              const countryInfo = getCountryDisplayInfo(defaultCountryCode);
                              return (
                                <Button
                                  {...rootProps}
                                  variant="outlined"
                                  fullWidth
                                  sx={{
                                    justifyContent: 'flex-start',
                                    textTransform: 'none',
                                    py: 1.5,
                                    gap: 1,
                                  }}
                                >
                                  <img
                                    src={`https://flagcdn.com/w20/${defaultCountryCode.toLowerCase()}.png`}
                                    alt={countryInfo.name}
                                    style={{ width: 20, height: 'auto' }}
                                  />
                                  <Typography variant="body2">
                                    +{countryInfo.dialCode} — {countryInfo.name}
                                  </Typography>
                                </Button>
                              );
                            }}
                          />
                        </Paper>

                        <Paper sx={{ p: 2 }}>
                          <Typography variant="body2" fontWeight={500} gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <PhoneIphone color="primary" /> {t('pages:campaigns.create.configureColumns.phoneTypeFilter')}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
                            {t('pages:campaigns.create.configureColumns.phoneTypeFilterHelp')}
                          </Typography>
                          <FormControl fullWidth size="small">
                            <Select
                              value={phoneTypeFilter}
                              onChange={(e) => setPhoneTypeFilter(e.target.value)}
                            >
                              <MenuItem value="all">
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <PhoneIphone sx={{ fontSize: 18 }} /> + <PhoneCallback sx={{ fontSize: 18 }} /> {t('pages:campaigns.create.configureColumns.allTypes')}
                                </Box>
                              </MenuItem>
                              <MenuItem value="mobile_only">
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <PhoneIphone sx={{ fontSize: 18 }} /> {t('pages:campaigns.create.configureColumns.mobileOnly')}
                                </Box>
                              </MenuItem>
                              <MenuItem value="landline_only">
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <PhoneCallback sx={{ fontSize: 18 }} /> {t('pages:campaigns.create.configureColumns.landlineOnly')}
                                </Box>
                              </MenuItem>
                            </Select>
                          </FormControl>
                        </Paper>
                      </Box>
                    </Paper>
                  ),
                },
              ]}
            />

            <Box sx={{ mt: 3, display: 'flex', justifyContent: 'space-between' }}>
              <Button
                startIcon={<ArrowBack />}
                onClick={() =>
                  setActiveStep(isAiCampaign ? STEP_AGENTS : STEP_BASIC_INFO)
                }
              >
                {t('pages:campaigns.create.back')}
              </Button>
              <Button
                variant="contained"
                endIcon={<ArrowForward />}
                onClick={processFile}
                disabled={!canProceedFromStep(STEP_UPLOAD_CONTACTS)}
              >
                {t('pages:campaigns.create.continue')}
              </Button>
            </Box>
          </CardContent>
        </Card>
      )}
      {/* Step 5: Configure Columns */}
      {activeStep === STEP_CONFIGURE_COLUMNS && (
        <Card>
          <CardContent sx={{ p: 4 }}>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Description color="primary" /> {t('pages:campaigns.create.configureColumns.title')}
            </Typography>

            <Paper sx={{ p: 2, mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
              <CheckCircle color="success" />
              <Box sx={{ flex: 1 }}>
                <Typography fontWeight={600}>{fileName}</Typography>
                <Typography variant="body2" color="text.secondary">
                  {t('pages:campaigns.create.configureColumns.contactsFound', { count: importedRowsCount })}
                </Typography>
              </Box>
            </Paper>

            {/* Header Row Configuration */}
            <Paper
              sx={{
                p: 3,
                mb: 3,
                bgcolor: theme.palette.mode === 'light' ? theme.palette.grey[200] : 'transparent',
              }}
              variant="outlined"
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={hasHeaderRow}
                    onChange={(e) => setHasHeaderRow(e.target.checked)}
                  />
                }
                label={
                  <Box>
                    <Typography variant="body2" fontWeight={500}>
                        {t('pages:campaigns.create.configureColumns.headerRow')}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {hasHeaderRow 
                          ? t('pages:campaigns.create.configureColumns.headerRowYes')
                          : t('pages:campaigns.create.configureColumns.headerRowNo')}
                    </Typography>
                  </Box>
                }
              />
                <Tooltip title={isEditingColumns ? t('common:close') : t('pages:campaigns.create.configureColumns.editColumnNames')} arrow>
                  <IconButton 
                    onClick={() => setIsEditingColumns(!isEditingColumns)}
                    color={isEditingColumns ? 'primary' : 'default'}
                    size="small"
                  >
                    <Edit fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
              
              {/* Column Name Editor - only visible when editing */}
              {isEditingColumns && (
                <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                    {t('pages:campaigns.create.configureColumns.columnNames')}
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {columns.map((col, idx) => (
                    <TextField
                      key={idx}
                      size="small"
                      value={columnNames[idx] ?? col}
                      onChange={(e) => {
                        const newName = e.target.value;
                        setColumnNames(prev => ({ ...prev, [idx]: newName }));
                        setColumns(prev => prev.map((c, i) => i === idx ? newName : c));
                        if (phoneColumns.includes(col)) {
                          setPhoneColumns(prev => prev.map(c => c === col ? newName : c));
                        }
                        if (variableColumns.includes(col)) {
                          setVariableColumns(prev => prev.map(c => c === col ? newName : c));
                        }
                        if (displayNameColumn === col) {
                          setDisplayNameColumn(newName);
                        }
                      }}
                      sx={{ 
                        width: 'auto',
                        '& .MuiInputBase-input': { 
                          fontSize: '0.875rem',
                          padding: '4px 8px',
                          minWidth: '80px',
                        }
                      }}
                      InputProps={{
                        sx: {
                          backgroundColor: phoneColumns.includes(col) ? 'primary.50' : 
                                          col === displayNameColumn ? 'success.50' : 
                                          variableColumns.includes(col) ? 'secondary.50' : 
                                          'background.paper',
                        }
                      }}
                    />
                  ))}
                </Box>
              </Box>
              )}
            </Paper>

            {/* Display Name Column */}
            <Paper
              sx={{
                p: 3,
                mb: 3,
                bgcolor: theme.palette.mode === 'light' ? theme.palette.grey[200] : 'transparent',
              }}
              variant="outlined"
            >
              <FormControl fullWidth size="small">
                <InputLabel>{t('pages:campaigns.create.configureColumns.displayNameColumnRequired')}</InputLabel>
                <Select
                  value={displayNameColumn || ''}
                  label={t('pages:campaigns.create.configureColumns.displayNameColumnRequired')}
                  onChange={(e) => setDisplayNameColumn(e.target.value || null)}
                >
                  {columns.map(col => (
                    <MenuItem key={col} value={col}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {col}
                        {phoneColumns.includes(col) && (
                          <Chip size="small" label={t('pages:campaigns.create.configureColumns.phoneColumns')} color="primary" sx={{ height: 20, fontSize: 10 }} />
                        )}
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                  {t('pages:campaigns.create.configureColumns.displayNameColumnHelp')}
                </Typography>
              </FormControl>
            </Paper>

            {isAiCampaign && selectedAgent && (
              <Paper
                sx={{
                  p: 3,
                  mb: 3,
                  bgcolor: theme.palette.mode === 'light' ? theme.palette.grey[200] : 'transparent',
                }}
                variant="outlined"
              >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, mb: 1.5, flexWrap: 'wrap' }}>
                  <Typography variant="subtitle2">
                    {t('pages:campaigns.create.configureColumns.requiredDynamicVariablesTitle', {
                      agent: selectedAgent.name,
                    })}
                  </Typography>
                  <Chip
                    size="small"
                    color={missingRequiredDynamicVariables.length > 0 ? 'warning' : 'success'}
                    label={selectedAgentRequiredVariables.length > 0
                      ? t('pages:campaigns.create.configureColumns.requiredDynamicVariablesSummary', {
                        matched: selectedAgentRequiredVariables.length - missingRequiredDynamicVariables.length,
                        total: selectedAgentRequiredVariables.length,
                      })
                      : t('pages:campaigns.create.configureColumns.optionalDynamicVariablesSummary', {
                        total: selectedAgentOptionalVariables.length,
                      })}
                  />
                </Box>
                {selectedAgentRequiredVariables.length === 0 ? (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    <Alert severity="info" icon={<Info />}>
                      {t('pages:campaigns.create.configureColumns.requiredDynamicVariablesEmpty')}
                    </Alert>
                    {selectedAgentOptionalVariables.length > 0 && (
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <Typography variant="caption" color="text.secondary">
                          {t('pages:campaigns.create.configureColumns.optionalDynamicVariablesHelp')}
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                          {selectedAgentOptionalVariables.map((variable) => (
                            <Chip
                              key={variable}
                              size="small"
                              label={variable}
                              color="default"
                              variant="outlined"
                            />
                          ))}
                        </Box>
                      </Box>
                    )}
                  </Box>
                ) : (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    <Typography variant="caption" color="text.secondary">
                      {t('pages:campaigns.create.configureColumns.requiredDynamicVariablesHelp')}
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {selectedAgentRequiredVariables.map((variable) => {
                        const isMissing = missingRequiredDynamicVariables.includes(variable);
                        return (
                          <Chip
                            key={variable}
                            size="small"
                            label={variable}
                            color={isMissing ? 'error' : 'success'}
                            variant={isMissing ? 'filled' : 'outlined'}
                          />
                        );
                      })}
                    </Box>
                    {selectedAgentOptionalVariables.length > 0 && (
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <Typography variant="caption" color="text.secondary">
                          {t('pages:campaigns.create.configureColumns.optionalDynamicVariablesHelp')}
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                          {selectedAgentOptionalVariables.map((variable) => (
                            <Chip
                              key={variable}
                              size="small"
                              label={variable}
                              color="default"
                              variant="outlined"
                            />
                          ))}
                        </Box>
                      </Box>
                    )}
                    {missingRequiredDynamicVariables.length > 0 && (
                      <Alert severity="error">
                        {t('pages:campaigns.create.errors.requiredDynamicVariablesMissing', {
                          variables: missingRequiredDynamicVariables.join(', '),
                        })}
                      </Alert>
                    )}
                  </Box>
                )}
              </Paper>
            )}

            <ButtonTabs
              tabs={[
                {
                  id: 'phone-columns',
                  label: t('pages:campaigns.create.configureColumns.phoneColumnsRequired'),
                  icon: <PhoneIcon fontSize="small" />,
                  content: (
                    <Paper
                      sx={{
                        p: 3,
                        bgcolor: theme.palette.mode === 'light' ? theme.palette.grey[200] : 'transparent',
                      }}
                      variant="outlined"
                    >
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                        {t('pages:campaigns.create.configureColumns.phoneColumnsHelp')}
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        {columns.map((col) => {
                          const isPhone = phoneColumns.includes(col);
                          return (
                            <Chip
                              key={col}
                              label={
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                  {isPhone && (
                                    <Typography variant="caption" sx={{ fontWeight: 700 }}>
                                      #{phoneColumns.indexOf(col) + 1}
                                    </Typography>
                                  )}
                                  {col}
                                </Box>
                              }
                              onClick={() => {
                                if (isPhone) {
                                  setPhoneColumns(prev => prev.filter(c => c !== col));
                                  setVariableColumns(prev => [...prev, col]);
                                } else {
                                  setPhoneColumns(prev => [...prev, col]);
                                  setVariableColumns(prev => prev.filter(c => c !== col));
                                }
                              }}
                              color={isPhone ? 'primary' : 'default'}
                              variant={isPhone ? 'filled' : 'outlined'}
                              icon={isPhone ? <PhoneIcon /> : undefined}
                            />
                          );
                        })}
                      </Box>
                      {phoneColumns.length > 1 && (
                        <Alert severity="info" sx={{ mt: 2 }} icon={<PhoneInTalk />}>
                          <Typography variant="body2">
                            <strong>{t('pages:campaigns.create.configureColumns.multiplePhones', { count: phoneColumns.length })}</strong> {t('pages:campaigns.create.configureColumns.multiplePhonesHelp', { order: phoneColumns.join(' → ') })}
                          </Typography>
                        </Alert>
                      )}
                    </Paper>
                  ),
                },
                {
                  id: 'variable-columns',
                  label: t('pages:campaigns.create.configureColumns.variableColumns'),
                  icon: <PsychologyIcon fontSize="small" />,
                  content: (
                    <Paper
                      sx={{
                        p: 3,
                        bgcolor: theme.palette.mode === 'light' ? theme.palette.grey[200] : 'transparent',
                      }}
                      variant="outlined"
                    >
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                        {t('pages:campaigns.create.configureColumns.variableColumnsHelp')}
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        {columns.filter(c => !phoneColumns.includes(c)).map(col => {
                          const isVariable = variableColumns.includes(col);
                          return (
                            <Chip
                              key={col}
                              label={col}
                              onClick={() => {
                                if (isVariable) {
                                  setVariableColumns(prev => prev.filter(c => c !== col));
                                } else {
                                  setVariableColumns(prev => [...prev, col]);
                                }
                              }}
                              color={isVariable ? 'secondary' : 'default'}
                              variant={isVariable ? 'filled' : 'outlined'}
                            />
                          );
                        })}
                      </Box>
                    </Paper>
                  ),
                },
              ]}
            />

            <Box sx={{ mt: 4, display: 'flex', justifyContent: 'space-between' }}>
              <Button startIcon={<ArrowBack />} onClick={() => setActiveStep(STEP_UPLOAD_CONTACTS)}>
                {t('pages:campaigns.create.back')}
              </Button>
              <Button
                variant="contained"
                endIcon={<ArrowForward />}
                onClick={processContacts}
                disabled={!canProceedFromStep(STEP_CONFIGURE_COLUMNS)}
              >
                {t('pages:campaigns.create.continue')}
              </Button>
            </Box>
          </CardContent>
        </Card>
      )}
      {/* Step 6: Automation */}
      {hasAutomationStep && activeStep === STEP_AUTOMATION && (
        <Card>
          <CardContent sx={{ p: 4 }}>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Code color="primary" /> {t('pages:campaigns.create.steps.automation')}
            </Typography>

            {renderSettingsSection(
              t('pages:campaigns.create.settings.scripts'),
              <Code color="primary" />,
              scripts.length === 0 ? (
                <Alert severity={hasSelectedAgentRequiringPreCall ? 'warning' : 'info'}>
                  {t('pages:campaigns.create.settings.noScripts')}{' '}
                  <Button size="small" onClick={() => navigate('/scripts/new')}>
                    {t('pages:campaigns.create.settings.createScript')}
                  </Button>
                </Alert>
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    {t('pages:campaigns.create.settings.scriptsHelp')}
                  </Typography>
                  {scripts.map(script => (
                    <Paper
                      key={script.id}
                      sx={{
                        p: 2,
                        cursor: 'pointer',
                        border: '2px solid',
                        borderColor: selectedScripts.includes(script.id) ? 'primary.main' : 'transparent',
                        bgcolor: selectedScripts.includes(script.id) ? alpha('#1976d2', 0.05) : 'background.paper',
                        '&:hover': { bgcolor: 'action.hover' },
                      }}
                      onClick={() => handleScriptToggle(script.id)}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                        <Checkbox
                          checked={selectedScripts.includes(script.id)}
                          onChange={() => handleScriptToggle(script.id)}
                        />
                        <Box>
                          <Typography fontWeight={600}>{script.name}</Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                            <Chip
                              label={scriptEventLabels[script.eventType] || script.eventType}
                              size="small"
                              variant="outlined"
                            />
                            {script.description && (
                              <Typography variant="caption" color="text.secondary">
                                {script.description.substring(0, 50)}{script.description.length > 50 ? '...' : ''}
                              </Typography>
                            )}
                          </Box>
                        </Box>
                      </Box>
                    </Paper>
                  ))}
                </Box>
              )
            )}

            <Box sx={{ mt: 4, display: 'flex', justifyContent: 'space-between' }}>
              <Button startIcon={<ArrowBack />} onClick={() => setActiveStep(STEP_CONFIGURE_COLUMNS)}>
                {t('pages:campaigns.create.back')}
              </Button>
              <Button
                variant="contained"
                endIcon={<ArrowForward />}
                onClick={() => setActiveStep(hasTransferStep ? STEP_TRANSFERS : STEP_CAMPAIGN_SETTINGS)}
                disabled={!canProceedFromStep(STEP_AUTOMATION)}
              >
                {t('pages:campaigns.create.continue')}
              </Button>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Step 7: Transfers */}
      {hasTransferStep && activeStep === STEP_TRANSFERS && (
        <Card>
          <CardContent sx={{ p: 4 }}>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <PhoneInTalk color="primary" /> {t('pages:campaigns.create.transfers.title')}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              {t('pages:campaigns.create.transfers.description')}
            </Typography>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
              <FormControl fullWidth>
                <InputLabel>{t('pages:campaigns.create.transfers.targetType')}</InputLabel>
                <Select
                  value={transferTargetType}
                  label={t('pages:campaigns.create.transfers.targetType')}
                  onChange={(event) => setTransferTargetType(event.target.value as CampaignTransferTargetType | '')}
                >
                  <MenuItem value="user">{t('pages:campaigns.create.transfers.targetTypeUser')}</MenuItem>
                  <MenuItem value="group" disabled={transferGroups.length === 0}>
                    {t('pages:campaigns.create.transfers.targetTypeGroup')}
                  </MenuItem>
                </Select>
              </FormControl>

              {transferTargetType === 'user' && (
                <FormControl fullWidth>
                  <InputLabel>{t('pages:campaigns.create.transfers.targetUser')}</InputLabel>
                  <Select
                    value={transferTargetId}
                    label={t('pages:campaigns.create.transfers.targetUser')}
                    onChange={(event) => setTransferTargetId(event.target.value)}
                  >
                    {transferUsers.map((entry) => (
                      <MenuItem key={entry.id} value={entry.id}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                          <Typography variant="body2">{entry.name}</Typography>
                          {entry.extension && (
                            <Chip
                              size="small"
                              label={t('pages:campaigns.create.transfers.userExtension', { extension: entry.extension })}
                              variant="outlined"
                            />
                          )}
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}

              {transferTargetType === 'group' && (
                <>
                  {transferGroups.length === 0 ? (
                    <Alert severity="warning">
                      {t('pages:campaigns.create.transfers.noGroups')}
                    </Alert>
                  ) : (
                    <FormControl fullWidth>
                      <InputLabel>{t('pages:campaigns.create.transfers.targetGroup')}</InputLabel>
                      <Select
                        value={transferTargetId}
                        label={t('pages:campaigns.create.transfers.targetGroup')}
                        onChange={(event) => setTransferTargetId(event.target.value)}
                      >
                        {transferGroups.map((entry) => (
                          <MenuItem key={entry.id} value={entry.id}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                              <Typography variant="body2">{entry.name}</Typography>
                              <Chip
                                size="small"
                                label={t('pages:campaigns.create.transfers.groupMembers', {
                                  count: entry.membersCount,
                                })}
                                variant="outlined"
                              />
                              <Chip
                                size="small"
                                label={t(
                                  `pages:campaigns.create.transfers.groupStrategy.${entry.strategy}`,
                                  { defaultValue: entry.strategy },
                                )}
                                variant="outlined"
                              />
                            </Box>
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                </>
              )}

              {transferTargetType === '' && (
                <Alert severity="info">
                  {t('pages:campaigns.create.transfers.selectionRequired')}
                </Alert>
              )}

              <Paper variant="outlined" sx={{ p: 2, bgcolor: 'background.default' }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={suspendWhenNoTransferOperatorAvailable}
                      onChange={(event) => setSuspendWhenNoTransferOperatorAvailable(event.target.checked)}
                    />
                  }
                  label={t('pages:campaigns.create.transfers.suspendWhenNoOperator')}
                />
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  {t('pages:campaigns.create.transfers.suspendWhenNoOperatorHelp')}
                </Typography>
              </Paper>
            </Box>

            <Box sx={{ mt: 4, display: 'flex', justifyContent: 'space-between' }}>
              <Button
                startIcon={<ArrowBack />}
                onClick={() =>
                  setActiveStep(hasAutomationStep ? STEP_AUTOMATION : STEP_CONFIGURE_COLUMNS)
                }
              >
                {t('pages:campaigns.create.back')}
              </Button>
              <Button
                variant="contained"
                endIcon={<ArrowForward />}
                onClick={() => setActiveStep(STEP_CAMPAIGN_SETTINGS)}
                disabled={!canProceedFromStep(STEP_TRANSFERS)}
              >
                {t('pages:campaigns.create.continue')}
              </Button>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Step 8: Campaign Settings */}
      {activeStep === STEP_CAMPAIGN_SETTINGS && (
        <Card>
          <CardContent sx={{ p: 4 }}>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Settings color="primary" /> {t('pages:campaigns.create.settings.title')}
              </Typography>

            <Paper sx={{ p: 2, mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
              <CheckCircle color="success" />
              <Box>
                <Typography fontWeight={600}>{t('pages:campaigns.create.settings.contactsReady', { count: importedRowsCount })}</Typography>
                <Typography variant="body2" color="text.secondary">
                  {isAiCampaign
                    ? `${t('pages:campaigns.create.basicInfo.ai_agent_provider')}: ${selectedProvider?.name || '—'} • ${t('pages:campaigns.create.agents.agent')}: ${availableAgents.find((agent) => agent.id === selectedAgentId)?.name || '—'}`
                    : `${t('pages:campaigns.create.basicInfo.sipTrunkPool')}: ${selectedSipTrunkPool?.name || '—'} • ${t('pages:campaigns.create.basicInfo.ivrAudio')}: ${reverseIvrAudioSummaryLabel} • ${t('pages:campaigns.create.basicInfo.ivrDtmfMap')}: ${formatNumber(ivrDtmfRules.length)}`}
                </Typography>
              </Box>
            </Paper>

            <ButtonTabs
              tabs={[
                {
                  id: 'concurrency',
                  label: t('pages:campaigns.create.settings.tabs.concurrency'),
                  icon: <Hub />,
                  content: renderSettingsSection(
                    t('pages:campaigns.create.settings.concurrency'),
                    <Hub color="primary" />,
                    <Box>
                      <Typography gutterBottom>
                        {t('pages:campaigns.create.settings.maxConcurrentCalls')}
                        {hasFiniteMaxConcurrent && ` (${t('pages:campaigns.create.settings.maxConcurrentCallsLimit', { limit: maxAllowedConcurrent })})`}
                      </Typography>
                      {hasFiniteMaxConcurrent ? (
                        <Slider
                          value={maxConcurrentCalls}
                          onChange={(_, newValue) => {
                            setMaxConcurrentCalls(clampMaxConcurrent(newValue as number));
                          }}
                          min={1}
                          max={maxAllowedConcurrent}
                          step={1}
                          marks={[
                            { value: 1, label: '1' },
                            { value: maxAllowedConcurrent, label: String(maxAllowedConcurrent) }
                          ]}
                          valueLabelDisplay="auto"
                        />
                      ) : maxAllowedConcurrent === null ? (
                        <TextField
                          fullWidth
                          size="small"
                          type="number"
                          value={maxConcurrentCalls}
                          onChange={(event) => {
                            const parsed = parseInt(event.target.value || '1', 10);
                            setMaxConcurrentCalls(Math.max(1, Number.isFinite(parsed) ? parsed : 1));
                          }}
                          inputProps={{ min: 1 }}
                        />
                      ) : (
                        <Typography variant="body2" color="error" sx={{ py: 2 }}>
                          {t('pages:campaigns.create.settings.noConcurrentAllowed')}
                        </Typography>
                      )}
                      <Typography variant="caption" color="text.secondary">
                        {t('pages:campaigns.create.settings.maxConcurrentCallsHelp')}
                      </Typography>
                    </Box>
                  ),
                },
                {
                  id: 'retries',
                  label: t('pages:campaigns.create.settings.tabs.retries'),
                  icon: <Repeat />,
                  content: renderSettingsSection(
                    t('pages:campaigns.create.settings.retries'),
                    <Repeat color="primary" />,
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                      <TextField
                        fullWidth
                        label={t('pages:campaigns.create.settings.maxRetries')}
                        type="number"
                        value={maxRetries}
                        onChange={(e) => setMaxRetries(Math.max(1, parseInt(e.target.value) || 1))}
                        inputProps={{ min: 1, max: 10 }}
                        helperText={t('pages:campaigns.create.settings.maxRetriesHelp')}
                      />

                      <Box sx={{ display: 'flex', gap: 2 }}>
                        <TextField
                          fullWidth
                          label={t('pages:campaigns.create.settings.retryDelayMin')}
                          type="number"
                          value={retryDelayMinSeconds}
                          onChange={(e) => setRetryDelayMinSeconds(Math.max(0, parseInt(e.target.value) || 0))}
                          inputProps={{ min: 0 }}
                          helperText={t('pages:campaigns.create.settings.retryDelayMinHelp')}
                        />
                        <TextField
                          fullWidth
                          label={t('pages:campaigns.create.settings.retryDelayMax')}
                          type="number"
                          value={retryDelayMaxSeconds}
                          onChange={(e) => setRetryDelayMaxSeconds(Math.max(0, parseInt(e.target.value) || 0))}
                          inputProps={{ min: 0 }}
                          helperText={t('pages:campaigns.create.settings.retryDelayMaxHelp')}
                        />
                      </Box>
                    </Box>
                  ),
                },
                {
                  id: 'schedule',
                  label: t('pages:campaigns.create.settings.tabs.schedule'),
                  icon: <Schedule />,
                  content: renderSettingsSection(
                    t('pages:campaigns.create.settings.schedule'),
                    <Schedule color="primary" />,
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      <Typography variant="body2" color="text.secondary">
                        {t('pages:campaigns.create.settings.scheduleHelp')}
                      </Typography>
                      
                      {scheduleEntries.map((entry, index) => {
                        const entryErrors = getEntryErrors(index);
                        const hasErrors = entryErrors.length > 0;
                        const { minTime, maxTime } = getTimeConstraints(entry);
                        
                        return (
                          <Paper 
                            key={index} 
                            sx={{ 
                              p: 2, 
                              bgcolor: 'background.default',
                              border: hasErrors ? '2px solid' : 'none',
                              borderColor: hasErrors ? 'error.main' : 'transparent',
                            }}
                          >
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                              <Typography variant="subtitle2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <AccessTime fontSize="small" /> {t('pages:campaigns.create.settings.scheduleEntry', { number: index + 1 })}
                              </Typography>
                              <IconButton 
                                size="small" 
                                onClick={() => {
                                  setScheduleEntries(prev => prev.filter((_, i) => i !== index));
                                }}
                                color="error"
                              >
                                <Delete fontSize="small" />
                              </IconButton>
                            </Box>
                            
                            <Box sx={{ mb: 2 }}>
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                                {t('pages:campaigns.create.settings.scheduleDays')}
                              </Typography>
                              <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                                {['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'].map(day => {
                                  const isSelected = entry.days.includes(day);
                                  const globalDay = tenantOperatingHours[day];
                                  const isGlobalEnabled = globalDay?.enabled ?? false;
                                  return (
                                    <Chip
                                      key={day}
                                      label={t(`common:days.${day}`)}
                                      size="small"
                                      disabled={!isGlobalEnabled}
                                      color={isSelected ? 'primary' : 'default'}
                                      variant={isSelected ? 'filled' : 'outlined'}
                                      onClick={() => {
                                        if (!isGlobalEnabled) return;
                                        setScheduleEntries(prev => prev.map((e, i) => {
                                          if (i !== index) return e;
                                          const newDays = e.days.includes(day)
                                            ? e.days.filter(d => d !== day)
                                            : [...e.days, day];
                                          const keepAllDay = Boolean(
                                            e.allDay
                                              && newDays.length > 0
                                              && newDays.every(selectedDay => tenantOperatingHours[selectedDay]?.allDay)
                                          );
                                          const fallbackDay = newDays[0] || day;
                                          return {
                                            ...e,
                                            days: newDays,
                                            allDay: keepAllDay,
                                            start: keepAllDay ? null : (e.start || tenantOperatingHours[fallbackDay]?.start || '09:00'),
                                            end: keepAllDay ? null : (e.end || tenantOperatingHours[fallbackDay]?.end || '18:00'),
                                          };
                                        }));
                                      }}
                                      sx={{ 
                                        cursor: isGlobalEnabled ? 'pointer' : 'not-allowed',
                                        opacity: isGlobalEnabled ? 1 : 0.5,
                                      }}
                                    />
                                  );
                                })}
                              </Box>
                            </Box>

                            {(() => {
                              const canUseAllDay = entry.days.length > 0
                                && entry.days.every(day => Boolean(tenantOperatingHours[day]?.enabled && tenantOperatingHours[day]?.allDay));
                              return (
                                <FormControlLabel
                                  control={
                                    <Switch
                                      checked={Boolean(entry.allDay)}
                                      disabled={!canUseAllDay}
                                      onChange={(event) => {
                                        const checked = event.target.checked;
                                        setScheduleEntries(prev => prev.map((ent, i) => {
                                          if (i !== index) return ent;
                                          const fallbackDay = ent.days[0] || 'mon';
                                          return {
                                            ...ent,
                                            allDay: checked,
                                            start: checked ? null : (ent.start || tenantOperatingHours[fallbackDay]?.start || '09:00'),
                                            end: checked ? null : (ent.end || tenantOperatingHours[fallbackDay]?.end || '18:00'),
                                          };
                                        }));
                                      }}
                                    />
                                  }
                                  label={t('pages:campaigns.create.settings.scheduleAllDay')}
                                  sx={{ mb: 1 }}
                                />
                              );
                            })()}

                            {!entry.allDay ? (
                              <LocalizationProvider dateAdapter={AdapterDayjs}>
                                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                                <TimePicker
                                  label={t('pages:campaigns.create.settings.scheduleStart')}
                                  value={entry.start ? dayjs(`2000-01-01T${entry.start}`) : null}
                                  onChange={(value: Dayjs | null) => {
                                    const timeStr = value ? value.format('HH:mm') : '';
                                    setScheduleEntries(prev => prev.map((ent, i) => 
                                      i === index ? { ...ent, start: timeStr } : ent
                                    ));
                                  }}
                                  ampm={false}
                                  minTime={minTime ? dayjs(`2000-01-01T${minTime}`) : undefined}
                                  maxTime={maxTime ? dayjs(`2000-01-01T${maxTime}`) : undefined}
                                  slotProps={{
                                    textField: { 
                                      size: 'small', 
                                      sx: { flex: 1 },
                                      error: entryErrors.some(e => e.type === 'time_invalid' || e.type === 'outside_limits'),
                                    }
                                  }}
                                />
                                <Typography variant="body2" color="text.secondary">—</Typography>
                                <TimePicker
                                  label={t('pages:campaigns.create.settings.scheduleEnd')}
                                  value={entry.end ? dayjs(`2000-01-01T${entry.end}`) : null}
                                  onChange={(value: Dayjs | null) => {
                                    const timeStr = value ? value.format('HH:mm') : '';
                                    setScheduleEntries(prev => prev.map((ent, i) => 
                                      i === index ? { ...ent, end: timeStr } : ent
                                    ));
                                  }}
                                  ampm={false}
                                  minTime={minTime ? dayjs(`2000-01-01T${minTime}`) : undefined}
                                  maxTime={maxTime ? dayjs(`2000-01-01T${maxTime}`) : undefined}
                                  slotProps={{
                                    textField: { 
                                      size: 'small', 
                                      sx: { flex: 1 },
                                      error: entryErrors.some(e => e.type === 'time_invalid' || e.type === 'outside_limits'),
                                    }
                                  }}
                                />
                                </Box>
                              </LocalizationProvider>
                            ) : null}
                            
                            {entry.days.length > 0 && (
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                                {t('pages:campaigns.create.settings.scheduleGlobalLimit')}: {' '}
                                {entry.days.map(day => {
                                  const globalDay = tenantOperatingHours[day];
                                  if (!globalDay?.enabled) return null;
                                  if (globalDay.allDay) {
                                    return `${t(`common:days.${day}`)} ${t('pages:campaigns.create.settings.scheduleAllDayShort')}`;
                                  }
                                  return `${t(`common:days.${day}`)} ${globalDay.start}-${globalDay.end}`;
                                }).filter(Boolean).join(', ')}
                              </Typography>
                            )}
                            
                            {entryErrors.length > 0 && (
                              <Box sx={{ mt: 1.5 }}>
                                {entryErrors.map((error, errorIdx) => (
                                  <Alert key={errorIdx} severity="error" sx={{ mb: 0.5, py: 0 }}>
                                    {error.message}
                                  </Alert>
                                ))}
                              </Box>
                            )}
                          </Paper>
                        );
                      })}
                      
                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        <Button
                          variant="outlined"
                          startIcon={<Add />}
                          onClick={() => {
                            const enabledDays = Object.entries(tenantOperatingHours)
                              .filter(([_, config]) => config.enabled)
                              .map(([day]) => day);
                            
                            const defaultDays = ['mon', 'tue', 'wed', 'thu', 'fri'].filter(d => enabledDays.includes(d));
                            const defaultStart = tenantOperatingHours[defaultDays[0] || 'mon']?.start || '09:00';
                            const defaultEnd = tenantOperatingHours[defaultDays[0] || 'mon']?.end || '18:00';
                            
                            setScheduleEntries(prev => [...prev, {
                              days: defaultDays.length > 0 ? defaultDays : [],
                              allDay: false,
                              start: defaultStart,
                              end: defaultEnd,
                            }]);
                          }}
                        >
                          {t('pages:campaigns.create.settings.addScheduleEntry')}
                        </Button>
                        
                        <Button
                          variant="text"
                          startIcon={<RestartAlt />}
                          onClick={resetScheduleToDefaults}
                          color="secondary"
                        >
                          {t('pages:campaigns.create.settings.scheduleResetToDefault')}
                        </Button>
                      </Box>
                      
                      {Object.keys(tenantOperatingHours).length === 0 && (
                        <Alert severity="warning">
                          {t('pages:campaigns.create.settings.noGlobalHours')}
                        </Alert>
                      )}
                      
                      {scheduleEntries.length === 0 && Object.keys(tenantOperatingHours).length > 0 && (
                        <Alert severity="warning">
                          {t('pages:campaigns.create.settings.noScheduleInfo')}
                        </Alert>
                      )}
                      
                      {scheduleErrors.length > 0 && (
                        <Alert severity="error" sx={{ mt: 1 }}>
                          {t('pages:campaigns.create.settings.scheduleValidationError')}
                        </Alert>
                      )}
                    </Box>
                  ),
                },
                {
                  id: 'notifications',
                  label: t('pages:campaigns.create.settings.tabs.notifications'),
                  icon: <Email />,
                  content: renderSettingsSection(
                    t('pages:campaigns.create.settings.notifications'),
                    <Email color="primary" />,
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      <CampaignConversionEmailControl
                        checked={automaticConversionEmailsEnabled}
                        onChange={setAutomaticConversionEmailsEnabled}
                        translationPrefix="pages:campaigns.create.settings"
                      />
                      <TextField
                        label={
                          <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                            <Typography component="span">{t('pages:campaigns.create.settings.notificationEmails')}</Typography>
                            <Tooltip title={t('pages:campaigns.create.settings.notificationEmailsTooltip')} arrow>
                              <HelpOutline sx={{ fontSize: 14, color: 'text.secondary', cursor: 'help' }} />
                            </Tooltip>
                          </Box>
                        }
                        value={notificationEmailsInput}
                        onChange={(event) => handleNotificationEmailsChange(event.target.value)}
                        onBlur={(event) => handleNotificationEmailsChange(event.target.value)}
                        placeholder={t('pages:campaigns.create.settings.notificationEmailsPlaceholder')}
                        helperText={
                          notificationEmailsError ||
                          t('pages:campaigns.create.settings.notificationEmailsHelper')
                        }
                        error={Boolean(notificationEmailsError)}
                        multiline
                        minRows={2}
                      />
                    </Box>
                  ),
                },
              ]}
            />

            {/* Upload/Submit Progress */}
            {loading && submitPhase && (
              <Box sx={{ mt: 3, p: 2, bgcolor: 'background.default', borderRadius: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {submitPhase === 'uploading' && <CloudUpload sx={{ fontSize: 18 }} />}
                    {submitPhase === 'creating' && <Build sx={{ fontSize: 18 }} />}
                    {submitPhase === 'configuring' && <Edit sx={{ fontSize: 18 }} />}
                    <Typography variant="body2" fontWeight={500}>
                      {submitPhase === 'uploading' && t('pages:campaigns.create.settings.uploadProgress')}
                      {submitPhase === 'creating' && t('pages:campaigns.create.settings.creatingCampaign')}
                      {submitPhase === 'configuring' && t('pages:campaigns.create.settings.configuringScripts')}
                    </Typography>
                  </Box>
                  {uploadProgress !== null && (
                    <Typography variant="body2" fontWeight={500} color="primary">
                      {formatPercent(uploadProgress)}
                    </Typography>
                  )}
                </Box>
                <LinearProgress 
                  variant={submitPhase === 'uploading' && uploadProgress !== null ? 'determinate' : 'indeterminate'}
                  value={uploadProgress ?? undefined}
                  sx={{ height: 8, borderRadius: 1 }}
                />
                {submitPhase === 'uploading' && (
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                    {fileData
                      ? t('pages:campaigns.create.settings.uploadingFile', {
                        name: fileData.name,
                        size: formatDecimal(fileData.size / 1024 / 1024, 2),
                      })
                      : ivrAudioFile
                        ? t('pages:campaigns.create.settings.uploadingFile', {
                          name: ivrAudioFile.name,
                          size: formatDecimal(ivrAudioFile.size / 1024 / 1024, 2),
                        })
                        : ''}
                  </Typography>
                )}
              </Box>
            )}

            <Box sx={{ mt: 4, display: 'flex', justifyContent: 'space-between' }}>
              <Button
                startIcon={<ArrowBack />}
                onClick={() =>
                  setActiveStep(
                    hasTransferStep
                      ? STEP_TRANSFERS
                      : hasAutomationStep
                        ? STEP_AUTOMATION
                        : STEP_CONFIGURE_COLUMNS,
                  )
                }
                disabled={loading}
              >
                {t('pages:campaigns.create.back')}
              </Button>
              <Button
                variant="contained"
                onClick={handleSubmit}
                disabled={
                  loading ||
                  !name ||
                  (isAiCampaign && !selectedAgentId) ||
                  (isAiCampaign && hasSelectedAgentRequiringAppointmentCalendar && !selectedAppointmentCalendarId) ||
                  (isReverseIvrCampaign && (!selectedSipTrunkPoolId || !hasSelectedReverseIvrAudio || !!ivrDtmfValidationErrorKey)) ||
                  scheduleEntries.length === 0 ||
                  scheduleErrors.length > 0
                }
              >
                {loading 
                  ? (submitPhase === 'uploading' ? t('pages:campaigns.create.uploading') : submitPhase === 'creating' ? t('pages:campaigns.create.creating') : t('pages:campaigns.create.configuring'))
                  : t('pages:campaigns.create.createCampaign')
                }
              </Button>
            </Box>
          </CardContent>
        </Card>
      )}
    </Box>
  );
}
